/**
 * THIS FILE IS AUTO-GENERATED. DO NOT EDIT MANUALLY.
 * Generated from ../../api/openapi.json
 * Generated by generate-shallow-selectors.ts
 * To regenerate, run the following command in the frontend folder:
 *    pnpm run generate:integration
 * Contact the Core Team if any issues arise.
 */

/* eslint-disable */
import { createSelector } from 'reselect'

import {
    _createSelectPlural,
    _createSelectSingular,
    _groupBy,
    _keyBy,
    type IncludeOf,
    type KeyValue,
    type PluralResult,
    type RelationsAsReturnTypes,
    type SelectByAttrs,
    type SelectByOptions,
    type SelectByPluralOptions,
    type SelectPluralFn,
    type SelectSingularFn,
    type StormQuery,
    type StormQueryArg,
    type StormQuerySingular,
    type StormQuerySingularArg,
    type UsePluralFn,
    type UseSingularFn,
    type WithRelations,
} from './shallow-selectors.helpers'
import { useStore, type StoreState } from '~/store/store'
import { shallow } from 'zustand/shallow'
import { locationResourceTypeId } from '~/utils/dips'
// ── Function Key Extractors ───────────────────────────────────────────────

const _extract_scheduledSurgeries_location_localKey = (entity: any): KeyValue =>
    entity.surgeryResources?.find((resource: any) => resource.resourceTypeId === locationResourceTypeId)?.shortName

// ── Hook Factories ────────────────────────────────────────────────────────

function _createUseSingular<T extends Record<string, unknown>, E extends string>(selector: SelectSingularFn<T, E>): UseSingularFn<T, E> {
    return ((arg1?: unknown, arg2?: unknown) => useStore(state => (selector as any)(state, arg1, arg2))) as UseSingularFn<T, E>
}

function _createUsePlural<T extends Record<string, unknown>, E extends string>(selector: SelectPluralFn<T, E>): UsePluralFn<T, E> {
    return ((arg1?: unknown, arg2?: unknown) => useStore(state => (selector as any)(state, arg1, arg2), shallow)) as UsePluralFn<T, E>
}
// ── Relationship Selectors ────────────────────────────────────────────────

/** departments → locations (hasManyThrough departmentLocationAssignments → locations) */
export const selectLocationsByDepartment = createSelector(
    (state: StoreState) => state.di.entities.departmentLocationAssignments,
    (state: StoreState) => state.di.entities.locations,
    (throughTable, targetTable) => {
        const throughIndex = _groupBy(Object.values(throughTable.byId), e => e.department_id)
        return (input: number | { id: number }) => {
            const key = typeof input === 'number' ? input : input.id
            const throughRecords = throughIndex.get(key) ?? []
            const results: NonNullable<(typeof targetTable.byId)[number]>[] = []
            for (const record of throughRecords) {
                const targetKey = record.location_id
                if (targetKey !== null && targetKey !== undefined) {
                    const target = targetTable.byId[targetKey as number]
                    if (target) results.push(target)
                }
            }
            return results
        }
    }
)

/** departments → practitioners (hasManyThrough departmentPractitionerAssignments → practitioners) */
export const selectPractitionersByDepartment = createSelector(
    (state: StoreState) => state.di.entities.departmentPractitionerAssignments,
    (state: StoreState) => state.di.entities.practitioners,
    (throughTable, targetTable) => {
        const throughIndex = _groupBy(Object.values(throughTable.byId), e => e.department_id)
        return (input: number | { id: number }) => {
            const key = typeof input === 'number' ? input : input.id
            const throughRecords = throughIndex.get(key) ?? []
            const results: NonNullable<(typeof targetTable.byId)[number]>[] = []
            for (const record of throughRecords) {
                const targetKey = record.practitioner_id
                if (targetKey !== null && targetKey !== undefined) {
                    const target = targetTable.byId[targetKey as number]
                    if (target) results.push(target)
                }
            }
            return results
        }
    }
)

/** departments → sections (hasMany from sections) */
export const selectSectionsByDepartment = createSelector(
    (state: StoreState) => state.di.entities.sections,
    ({ byId }) => {
        const index = _groupBy(Object.values(byId), e => e.department_id)
        return (input: number | { id: number }) => index.get(typeof input === 'number' ? input : input.id) ?? []
    }
)

/** departmentPractitionerAssignments → department (hasOne from departments) */
export const selectDepartmentByDepartmentPractitionerAssignment = createSelector(
    (state: StoreState) => state.di.entities.departments,
    ({ byId }) =>
        (input: number | { department_id: number }) =>
            byId[(typeof input === 'number' ? input : input.department_id) as number]
)

/** departmentPractitionerAssignments → practitioner (hasOne from practitioners) */
export const selectPractitionerByDepartmentPractitionerAssignment = createSelector(
    (state: StoreState) => state.di.entities.practitioners,
    ({ byId }) =>
        (input: number | { practitioner_id: number }) =>
            byId[(typeof input === 'number' ? input : input.practitioner_id) as number]
)

/** locations → departments (hasManyThrough departmentLocationAssignments → departments) */
export const selectDepartmentsByLocation = createSelector(
    (state: StoreState) => state.di.entities.departmentLocationAssignments,
    (state: StoreState) => state.di.entities.departments,
    (throughTable, targetTable) => {
        const throughIndex = _groupBy(Object.values(throughTable.byId), e => e.location_id)
        return (input: number | { id: number }) => {
            const key = typeof input === 'number' ? input : input.id
            const throughRecords = throughIndex.get(key) ?? []
            const results: NonNullable<(typeof targetTable.byId)[number]>[] = []
            for (const record of throughRecords) {
                const targetKey = record.department_id
                if (targetKey !== null && targetKey !== undefined) {
                    const target = targetTable.byId[targetKey as number]
                    if (target) results.push(target)
                }
            }
            return results
        }
    }
)

/** locations → dipsResource (hasOne from dipsResources) */
export const selectDipsResourceByLocation = createSelector(
    (state: StoreState) => state.dips.entities.dipsResources,
    ({ byId }) => {
        const index = _keyBy(Object.values(byId), e => e.shortName)
        return (input: string | { room_code: string }) => index.get(typeof input === 'string' ? input : input.room_code)
    }
)

/** locations → postOps (hasManyThrough locationPostOpAssociations → postOps) */
export const selectPostOpsByLocation = createSelector(
    (state: StoreState) => state.di.entities.locationPostOpAssociations,
    (state: StoreState) => state.di.entities.postOps,
    (throughTable, targetTable) => {
        const throughIndex = _groupBy(Object.values(throughTable.byId), e => e.location_id)
        return (input: number | { id: number }) => {
            const key = typeof input === 'number' ? input : input.id
            const throughRecords = throughIndex.get(key) ?? []
            const results: NonNullable<(typeof targetTable.byId)[number]>[] = []
            for (const record of throughRecords) {
                const targetKey = record.post_op_id
                if (targetKey !== null && targetKey !== undefined) {
                    const target = targetTable.byId[targetKey as number]
                    if (target) results.push(target)
                }
            }
            return results
        }
    }
)

/** locations → locationProfiles (hasMany from locationProfiles) */
export const selectLocationProfilesByLocation = createSelector(
    (state: StoreState) => state.di.entities.locationProfiles,
    ({ byId }) => {
        const index = _groupBy(Object.values(byId), e => e.location_id)
        return (input: number | { id: number }) => index.get(typeof input === 'number' ? input : input.id) ?? []
    }
)

/** planningPeriods → prioritizedPatientGroups (hasMany from prioritizedPatientGroups) */
export const selectPrioritizedPatientGroupsByPlanningPeriod = createSelector(
    (state: StoreState) => state.di.entities.prioritizedPatientGroups,
    ({ byId }) => {
        const index = _groupBy(Object.values(byId), e => e.planning_period_id)
        return (input: number | { id: number }) => index.get(typeof input === 'number' ? input : input.id) ?? []
    }
)

/** practitioners → dipsResource (hasOne from dipsResources) */
export const selectDipsResourceByPractitioner = createSelector(
    (state: StoreState) => state.dips.entities.dipsResources,
    ({ byId }) => {
        const index = _keyBy(Object.values(byId), e => e.shortName)
        return (input: string | { short_name: string }) => index.get(typeof input === 'string' ? input : input.short_name)
    }
)

/** practitioners → departments (hasManyThrough departmentPractitionerAssignments → departments) */
export const selectDepartmentsByPractitioner = createSelector(
    (state: StoreState) => state.di.entities.departmentPractitionerAssignments,
    (state: StoreState) => state.di.entities.departments,
    (throughTable, targetTable) => {
        const throughIndex = _groupBy(Object.values(throughTable.byId), e => e.practitioner_id)
        return (input: number | { id: number }) => {
            const key = typeof input === 'number' ? input : input.id
            const throughRecords = throughIndex.get(key) ?? []
            const results: NonNullable<(typeof targetTable.byId)[number]>[] = []
            for (const record of throughRecords) {
                const targetKey = record.department_id
                if (targetKey !== null && targetKey !== undefined) {
                    const target = targetTable.byId[targetKey as number]
                    if (target) results.push(target)
                }
            }
            return results
        }
    }
)

/** practitioners → departmentAssignments (hasMany from departmentPractitionerAssignments) */
export const selectDepartmentAssignmentsByPractitioner = createSelector(
    (state: StoreState) => state.di.entities.departmentPractitionerAssignments,
    ({ byId }) => {
        const index = _groupBy(Object.values(byId), e => e.practitioner_id)
        return (input: number | { id: number }) => index.get(typeof input === 'number' ? input : input.id) ?? []
    }
)

/** practitioners → servicePeriods (hasMany from practitionerServicePeriods) */
export const selectServicePeriodsByPractitioner = createSelector(
    (state: StoreState) => state.di.entities.practitionerServicePeriods,
    ({ byId }) => {
        const index = _groupBy(Object.values(byId), e => e.practitioner_id)
        return (input: number | { id: number }) => index.get(typeof input === 'number' ? input : input.id) ?? []
    }
)

/** practitioners → practitionerSchedules (hasMany from practitionerSchedules) */
export const selectPractitionerSchedulesByPractitioner = createSelector(
    (state: StoreState) => state.di.entities.practitionerSchedules,
    ({ byId }) => {
        const index = _groupBy(Object.values(byId), e => e.practitioner_id)
        return (input: number | { id: number }) => index.get(typeof input === 'number' ? input : input.id) ?? []
    }
)

/** practitioners → practitionerProfiles (hasMany from practitionerProfiles) */
export const selectPractitionerProfilesByPractitioner = createSelector(
    (state: StoreState) => state.di.entities.practitionerProfiles,
    ({ byId }) => {
        const index = _groupBy(Object.values(byId), e => e.practitioner_id)
        return (input: number | { id: number }) => index.get(typeof input === 'number' ? input : input.id) ?? []
    }
)

/** practitionerServicePeriods → practitioner (hasOne from practitioners) */
export const selectPractitionerByPractitionerServicePeriod = createSelector(
    (state: StoreState) => state.di.entities.practitioners,
    ({ byId }) =>
        (input: number | { practitioner_id: number }) =>
            byId[(typeof input === 'number' ? input : input.practitioner_id) as number]
)

/** locationScheduleSpecialities → location (hasOne from locations) */
export const selectLocationByLocationScheduleSpeciality = createSelector(
    (state: StoreState) => state.di.entities.locations,
    ({ byId }) =>
        (input: number | { location_id: number }) =>
            byId[(typeof input === 'number' ? input : input.location_id) as number]
)

/** locationScheduleSpecialities → speciality (hasOne from specialities) */
export const selectSpecialityByLocationScheduleSpeciality = createSelector(
    (state: StoreState) => state.di.entities.specialities,
    ({ byId }) =>
        (input: number | { speciality_id: number }) =>
            byId[(typeof input === 'number' ? input : input.speciality_id) as number]
)

/** practitionerScheduleLocations → practitioner (hasOne from practitioners) */
export const selectPractitionerByPractitionerScheduleLocation = createSelector(
    (state: StoreState) => state.di.entities.practitioners,
    ({ byId }) =>
        (input: number | { practitioner_id: number }) =>
            byId[(typeof input === 'number' ? input : input.practitioner_id) as number]
)

/** practitionerScheduleLocations → location (hasOne from locations) */
export const selectLocationByPractitionerScheduleLocation = createSelector(
    (state: StoreState) => state.di.entities.locations,
    ({ byId }) =>
        (input: number | { location_id: number }) =>
            byId[(typeof input === 'number' ? input : input.location_id) as number]
)

/** practitionerScheduleStatuses → practitioner (hasOne from practitioners) */
export const selectPractitionerByPractitionerScheduleStatus = createSelector(
    (state: StoreState) => state.di.entities.practitioners,
    ({ byId }) =>
        (input: number | { practitioner_id: number }) =>
            byId[(typeof input === 'number' ? input : input.practitioner_id) as number]
)

/** practitionerScheduleStatuses → definition (hasOne from practitionerStatusDefinitions) */
export const selectDefinitionByPractitionerScheduleStatus = createSelector(
    (state: StoreState) => state.di.entities.practitionerStatusDefinitions,
    ({ byId }) => {
        const index = _keyBy(Object.values(byId), e => e.code)
        return (input: string | { status_code: string }) => index.get(typeof input === 'string' ? input : input.status_code)
    }
)

/** statusDefinitionDepartmentAssignments → department (hasOne from departments) */
export const selectDepartmentByStatusDefinitionDepartmentAssignment = createSelector(
    (state: StoreState) => state.di.entities.departments,
    ({ byId }) =>
        (input: number | { department_id: number }) =>
            byId[(typeof input === 'number' ? input : input.department_id) as number]
)

/** statusDefinitionDepartmentAssignments → statusDefinition (hasOne from practitionerStatusDefinitions) */
export const selectStatusDefinitionByStatusDefinitionDepartmentAssignment = createSelector(
    (state: StoreState) => state.di.entities.practitionerStatusDefinitions,
    ({ byId }) => {
        const index = _keyBy(Object.values(byId), e => e.code)
        return (input: string | { status_code: string }) => index.get(typeof input === 'string' ? input : input.status_code)
    }
)

/** practitionerStatusDefinitions → departments (hasManyThrough statusDefinitionDepartmentAssignments → departments) */
export const selectDepartmentsByPractitionerStatusDefinition = createSelector(
    (state: StoreState) => state.di.entities.statusDefinitionDepartmentAssignments,
    (state: StoreState) => state.di.entities.departments,
    (throughTable, targetTable) => {
        const throughIndex = _groupBy(Object.values(throughTable.byId), e => e.status_code)
        return (input: string | { code: string }) => {
            const key = typeof input === 'string' ? input : input.code
            const throughRecords = throughIndex.get(key) ?? []
            const results: NonNullable<(typeof targetTable.byId)[number]>[] = []
            for (const record of throughRecords) {
                const targetKey = record.department_id
                if (targetKey !== null && targetKey !== undefined) {
                    const target = targetTable.byId[targetKey as number]
                    if (target) results.push(target)
                }
            }
            return results
        }
    }
)

/** locationScheduleSpecialityActivityLogs → speciality (hasOne from specialities) */
export const selectSpecialityByLocationScheduleSpecialityActivityLog = createSelector(
    (state: StoreState) => state.di.entities.specialities,
    ({ byId }) =>
        (input: number | { speciality_id: number }) =>
            byId[(typeof input === 'number' ? input : input.speciality_id) as number]
)

/** practitionerScheduleStatusActivityLogs → statusDefinition (hasOne from practitionerStatusDefinitions) */
export const selectStatusDefinitionByPractitionerScheduleStatusActivityLog = createSelector(
    (state: StoreState) => state.di.entities.practitionerStatusDefinitions,
    ({ byId }) => {
        const index = _keyBy(Object.values(byId), e => e.code)
        return (input: string | { status_value: string }) => index.get(typeof input === 'string' ? input : input.status_value)
    }
)

/** practitionerScheduleLocationActivityLogs → location (hasOne from locations) */
export const selectLocationByPractitionerScheduleLocationActivityLog = createSelector(
    (state: StoreState) => state.di.entities.locations,
    ({ byId }) =>
        (input: number | { location_id: number }) =>
            byId[(typeof input === 'number' ? input : input.location_id) as number]
)

/** recurringAssignments → practitioner (hasOne from practitioners) */
export const selectPractitionerByRecurringAssignment = createSelector(
    (state: StoreState) => state.di.entities.practitioners,
    ({ byId }) =>
        (input: number | { practitioner_id: number }) =>
            byId[(typeof input === 'number' ? input : input.practitioner_id) as number]
)

/** recurringAssignments → monday_status (hasOne from practitionerStatusDefinitions) */
export const selectMondayStatusByRecurringAssignment = createSelector(
    (state: StoreState) => state.di.entities.practitionerStatusDefinitions,
    ({ byId }) => {
        const index = _keyBy(Object.values(byId), e => e.code)
        return (input: string | { monday_status_code: string }) => index.get(typeof input === 'string' ? input : input.monday_status_code)
    }
)

/** recurringAssignments → tuesday_status (hasOne from practitionerStatusDefinitions) */
export const selectTuesdayStatusByRecurringAssignment = createSelector(
    (state: StoreState) => state.di.entities.practitionerStatusDefinitions,
    ({ byId }) => {
        const index = _keyBy(Object.values(byId), e => e.code)
        return (input: string | { tuesday_status_code: string }) => index.get(typeof input === 'string' ? input : input.tuesday_status_code)
    }
)

/** recurringAssignments → wednesday_status (hasOne from practitionerStatusDefinitions) */
export const selectWednesdayStatusByRecurringAssignment = createSelector(
    (state: StoreState) => state.di.entities.practitionerStatusDefinitions,
    ({ byId }) => {
        const index = _keyBy(Object.values(byId), e => e.code)
        return (input: string | { wednesday_status_code: string }) => index.get(typeof input === 'string' ? input : input.wednesday_status_code)
    }
)

/** recurringAssignments → thursday_status (hasOne from practitionerStatusDefinitions) */
export const selectThursdayStatusByRecurringAssignment = createSelector(
    (state: StoreState) => state.di.entities.practitionerStatusDefinitions,
    ({ byId }) => {
        const index = _keyBy(Object.values(byId), e => e.code)
        return (input: string | { thursday_status_code: string }) => index.get(typeof input === 'string' ? input : input.thursday_status_code)
    }
)

/** recurringAssignments → friday_status (hasOne from practitionerStatusDefinitions) */
export const selectFridayStatusByRecurringAssignment = createSelector(
    (state: StoreState) => state.di.entities.practitionerStatusDefinitions,
    ({ byId }) => {
        const index = _keyBy(Object.values(byId), e => e.code)
        return (input: string | { friday_status_code: string }) => index.get(typeof input === 'string' ? input : input.friday_status_code)
    }
)

/** recurringAssignments → saturday_status (hasOne from practitionerStatusDefinitions) */
export const selectSaturdayStatusByRecurringAssignment = createSelector(
    (state: StoreState) => state.di.entities.practitionerStatusDefinitions,
    ({ byId }) => {
        const index = _keyBy(Object.values(byId), e => e.code)
        return (input: string | { saturday_status_code: string }) => index.get(typeof input === 'string' ? input : input.saturday_status_code)
    }
)

/** recurringAssignments → sunday_status (hasOne from practitionerStatusDefinitions) */
export const selectSundayStatusByRecurringAssignment = createSelector(
    (state: StoreState) => state.di.entities.practitionerStatusDefinitions,
    ({ byId }) => {
        const index = _keyBy(Object.values(byId), e => e.code)
        return (input: string | { sunday_status_code: string }) => index.get(typeof input === 'string' ? input : input.sunday_status_code)
    }
)

/** recurringAssignments → monday_room (hasOne from locations) */
export const selectMondayRoomByRecurringAssignment = createSelector(
    (state: StoreState) => state.di.entities.locations,
    ({ byId }) => {
        const index = _keyBy(Object.values(byId), e => e.room_code)
        return (input: string | { monday_room_code: string }) => index.get(typeof input === 'string' ? input : input.monday_room_code)
    }
)

/** recurringAssignments → tuesday_room (hasOne from locations) */
export const selectTuesdayRoomByRecurringAssignment = createSelector(
    (state: StoreState) => state.di.entities.locations,
    ({ byId }) => {
        const index = _keyBy(Object.values(byId), e => e.room_code)
        return (input: string | { tuesday_room_code: string }) => index.get(typeof input === 'string' ? input : input.tuesday_room_code)
    }
)

/** recurringAssignments → wednesday_room (hasOne from locations) */
export const selectWednesdayRoomByRecurringAssignment = createSelector(
    (state: StoreState) => state.di.entities.locations,
    ({ byId }) => {
        const index = _keyBy(Object.values(byId), e => e.room_code)
        return (input: string | { wednesday_room_code: string }) => index.get(typeof input === 'string' ? input : input.wednesday_room_code)
    }
)

/** recurringAssignments → thursday_room (hasOne from locations) */
export const selectThursdayRoomByRecurringAssignment = createSelector(
    (state: StoreState) => state.di.entities.locations,
    ({ byId }) => {
        const index = _keyBy(Object.values(byId), e => e.room_code)
        return (input: string | { thursday_room_code: string }) => index.get(typeof input === 'string' ? input : input.thursday_room_code)
    }
)

/** recurringAssignments → friday_room (hasOne from locations) */
export const selectFridayRoomByRecurringAssignment = createSelector(
    (state: StoreState) => state.di.entities.locations,
    ({ byId }) => {
        const index = _keyBy(Object.values(byId), e => e.room_code)
        return (input: string | { friday_room_code: string }) => index.get(typeof input === 'string' ? input : input.friday_room_code)
    }
)

/** recurringAssignments → saturday_room (hasOne from locations) */
export const selectSaturdayRoomByRecurringAssignment = createSelector(
    (state: StoreState) => state.di.entities.locations,
    ({ byId }) => {
        const index = _keyBy(Object.values(byId), e => e.room_code)
        return (input: string | { saturday_room_code: string }) => index.get(typeof input === 'string' ? input : input.saturday_room_code)
    }
)

/** recurringAssignments → sunday_room (hasOne from locations) */
export const selectSundayRoomByRecurringAssignment = createSelector(
    (state: StoreState) => state.di.entities.locations,
    ({ byId }) => {
        const index = _keyBy(Object.values(byId), e => e.room_code)
        return (input: string | { sunday_room_code: string }) => index.get(typeof input === 'string' ? input : input.sunday_room_code)
    }
)

/** blockSchedules → location (hasOne from locations) */
export const selectLocationByBlockSchedule = createSelector(
    (state: StoreState) => state.di.entities.locations,
    ({ byId }) =>
        (input: number | { location_id: number }) =>
            byId[(typeof input === 'number' ? input : input.location_id) as number]
)

/** locationSchedules → comments (hasMany from comments) */
export const selectCommentsByLocationSchedule = createSelector(
    (state: StoreState) => state.di.entities.comments,
    ({ byId }) => {
        const index = _groupBy(Object.values(byId), e => e.location_schedule_id)
        return (input: number | { id: number }) => index.get(typeof input === 'number' ? input : input.id) ?? []
    }
)

/** sections → department (hasOne from departments) */
export const selectDepartmentBySection = createSelector(
    (state: StoreState) => state.di.entities.departments,
    ({ byId }) =>
        (input: number | { department_id: number }) =>
            byId[(typeof input === 'number' ? input : input.department_id) as number]
)

/** sections → speciality (hasOne from specialities) */
export const selectSpecialityBySection = createSelector(
    (state: StoreState) => state.di.entities.specialities,
    ({ byId }) =>
        (input: number | { speciality_id: number }) =>
            byId[(typeof input === 'number' ? input : input.speciality_id) as number]
)

/** scheduledSurgeries → surgeryFolderMetadata (hasOne from surgeryFolderMetadata) */
export const selectSurgeryFolderMetadataByScheduledSurgery = createSelector(
    (state: StoreState) => state.di.entities.surgeryFolderMetadata,
    ({ byId }) => {
        const index = _keyBy(Object.values(byId), e => e.folder_id)
        return (input: string | { surgeryOrderDetails?: { folderId?: string } }) => {
            const key = typeof input === 'string' ? input : input?.surgeryOrderDetails?.folderId
            return key !== undefined && key !== null ? index.get(key) : undefined
        }
    }
)

/** scheduledSurgeries → section (hasOne from sections) */
export const selectSectionByScheduledSurgery = createSelector(
    (state: StoreState) => state.di.entities.sections,
    ({ byId }) => {
        const index = _keyBy(Object.values(byId), e => e.dips_section_id)
        return (input: number | { contact?: { section?: { id?: number } } }) => {
            const key = typeof input === 'number' ? input : input?.contact?.section?.id
            return key !== undefined && key !== null ? index.get(key) : undefined
        }
    }
)

/** scheduledSurgeries → location (hasOne from locations) */
export const selectLocationByScheduledSurgery = createSelector(
    (state: StoreState) => state.di.entities.locations,
    ({ byId }) => {
        const index = _keyBy(Object.values(byId), e => e.room_code)
        return (input: string | Record<string, unknown>) => {
            const key = typeof input === 'string' ? input : _extract_scheduledSurgeries_location_localKey(input)
            return key !== undefined && key !== null ? index.get(key) : undefined
        }
    }
)

/** scheduledSurgeries → surgeryMetadataSuggestions (hasOne from surgeryMetadataSuggestions) */
export const selectSurgeryMetadataSuggestionsByScheduledSurgery = createSelector(
    (state: StoreState) => state.di.entities.surgeryMetadataSuggestions,
    ({ byId }) => {
        const index = _keyBy(Object.values(byId), e => e.folder_id)
        return (input: string | { surgeryOrderDetails?: { folderId?: string } }) => {
            const key = typeof input === 'string' ? input : input?.surgeryOrderDetails?.folderId
            return key !== undefined && key !== null ? index.get(key) : undefined
        }
    }
)

/** unScheduledSurgeries → surgeryFolderMetadata (hasOne from surgeryFolderMetadata) */
export const selectSurgeryFolderMetadataByUnScheduledSurgery = createSelector(
    (state: StoreState) => state.di.entities.surgeryFolderMetadata,
    ({ byId }) => {
        const index = _keyBy(Object.values(byId), e => e.folder_id)
        return (input: string | { surgeryOrderDetails?: { folderId?: string } }) => {
            const key = typeof input === 'string' ? input : input?.surgeryOrderDetails?.folderId
            return key !== undefined && key !== null ? index.get(key) : undefined
        }
    }
)

/** unScheduledSurgeries → section (hasOne from sections) */
export const selectSectionByUnScheduledSurgery = createSelector(
    (state: StoreState) => state.di.entities.sections,
    ({ byId }) => {
        const index = _keyBy(Object.values(byId), e => e.dips_section_id)
        return (input: number | { contact?: { section?: { id?: number } } }) => {
            const key = typeof input === 'number' ? input : input?.contact?.section?.id
            return key !== undefined && key !== null ? index.get(key) : undefined
        }
    }
)

/** unScheduledSurgeries → surgeryMetadataSuggestions (hasOne from surgeryMetadataSuggestions) */
export const selectSurgeryMetadataSuggestionsByUnScheduledSurgery = createSelector(
    (state: StoreState) => state.di.entities.surgeryMetadataSuggestions,
    ({ byId }) => {
        const index = _keyBy(Object.values(byId), e => e.folder_id)
        return (input: string | { surgeryOrderDetails?: { folderId?: string } }) => {
            const key = typeof input === 'string' ? input : input?.surgeryOrderDetails?.folderId
            return key !== undefined && key !== null ? index.get(key) : undefined
        }
    }
)

/** specialities → sections (hasMany from sections) */
export const selectSectionsBySpeciality = createSelector(
    (state: StoreState) => state.di.entities.sections,
    ({ byId }) => {
        const index = _groupBy(Object.values(byId), e => e.speciality_id)
        return (input: number | { id: number }) => index.get(typeof input === 'number' ? input : input.id) ?? []
    }
)

/** surgeryTypeGroups → ageRestrictions (hasManyThrough surgeryTypeGroupAgeRestrictions → ageGroups) */
export const selectAgeRestrictionsBySurgeryTypeGroup = createSelector(
    (state: StoreState) => state.di.entities.surgeryTypeGroupAgeRestrictions,
    (state: StoreState) => state.di.entities.ageGroups,
    (throughTable, targetTable) => {
        const throughIndex = _groupBy(Object.values(throughTable.byId), e => e.surgery_type_group_id)
        return (input: number | { id: number }) => {
            const key = typeof input === 'number' ? input : input.id
            const throughRecords = throughIndex.get(key) ?? []
            const results: NonNullable<(typeof targetTable.byId)[number]>[] = []
            for (const record of throughRecords) {
                const targetKey = record.age_group_id
                if (targetKey !== null && targetKey !== undefined) {
                    const target = targetTable.byId[targetKey as number]
                    if (target) results.push(target)
                }
            }
            return results
        }
    }
)

/** surgeryTypeGroups → specialities (hasManyThrough surgeryTypeGroupSpecialities → specialities) */
export const selectSpecialitiesBySurgeryTypeGroup = createSelector(
    (state: StoreState) => state.di.entities.surgeryTypeGroupSpecialities,
    (state: StoreState) => state.di.entities.specialities,
    (throughTable, targetTable) => {
        const throughIndex = _groupBy(Object.values(throughTable.byId), e => e.surgery_type_group_id)
        return (input: number | { id: number }) => {
            const key = typeof input === 'number' ? input : input.id
            const throughRecords = throughIndex.get(key) ?? []
            const results: NonNullable<(typeof targetTable.byId)[number]>[] = []
            for (const record of throughRecords) {
                const targetKey = record.speciality_id
                if (targetKey !== null && targetKey !== undefined) {
                    const target = targetTable.byId[targetKey as number]
                    if (target) results.push(target)
                }
            }
            return results
        }
    }
)

/** surgeryTypeGroups → subgroups (hasManyThrough surgeryTypeGroupHierarchies → surgeryTypeGroups) */
export const selectSubgroupsBySurgeryTypeGroup = createSelector(
    (state: StoreState) => state.di.entities.surgeryTypeGroupHierarchies,
    (state: StoreState) => state.di.entities.surgeryTypeGroups,
    (throughTable, targetTable) => {
        const throughIndex = _groupBy(Object.values(throughTable.byId), e => e.parent_surgery_type_group_id)
        return (input: number | { id: number }) => {
            const key = typeof input === 'number' ? input : input.id
            const throughRecords = throughIndex.get(key) ?? []
            const results: NonNullable<(typeof targetTable.byId)[number]>[] = []
            for (const record of throughRecords) {
                const targetKey = record.child_surgery_type_group_id
                if (targetKey !== null && targetKey !== undefined) {
                    const target = targetTable.byId[targetKey as number]
                    if (target) results.push(target)
                }
            }
            return results
        }
    }
)

/** surgeryTypeGroups → surgeryTypes (hasMany from hospitalSurgeryTypeGroupAssociations) */
export const selectSurgeryTypesBySurgeryTypeGroup = createSelector(
    (state: StoreState) => state.di.entities.hospitalSurgeryTypeGroupAssociations,
    ({ byId }) => {
        const index = _groupBy(Object.values(byId), e => e.surgery_type_group_id)
        return (input: number | { id: number }) => index.get(typeof input === 'number' ? input : input.id) ?? []
    }
)

/** staffingPlanExecutions → department (hasOne from departments) */
export const selectDepartmentByStaffingPlanExecution = createSelector(
    (state: StoreState) => state.di.entities.departments,
    ({ byId }) =>
        (input: number | { department_id: number }) =>
            byId[(typeof input === 'number' ? input : input.department_id) as number]
)

/** comments → locationSchedule (hasOne from locationSchedules) */
export const selectLocationScheduleByComment = createSelector(
    (state: StoreState) => state.di.entities.locationSchedules,
    ({ byId }) =>
        (input: number | { location_schedule_id: number }) =>
            byId[(typeof input === 'number' ? input : input.location_schedule_id) as number]
)

/** postOps → locations (hasManyThrough locationPostOpAssociations → locations) */
export const selectLocationsByPostOp = createSelector(
    (state: StoreState) => state.di.entities.locationPostOpAssociations,
    (state: StoreState) => state.di.entities.locations,
    (throughTable, targetTable) => {
        const throughIndex = _groupBy(Object.values(throughTable.byId), e => e.post_op_id)
        return (input: number | { id: number }) => {
            const key = typeof input === 'number' ? input : input.id
            const throughRecords = throughIndex.get(key) ?? []
            const results: NonNullable<(typeof targetTable.byId)[number]>[] = []
            for (const record of throughRecords) {
                const targetKey = record.location_id
                if (targetKey !== null && targetKey !== undefined) {
                    const target = targetTable.byId[targetKey as number]
                    if (target) results.push(target)
                }
            }
            return results
        }
    }
)

/** postOps → capacityPeriods (hasMany from postOpCapacityPeriods) */
export const selectCapacityPeriodsByPostOp = createSelector(
    (state: StoreState) => state.di.entities.postOpCapacityPeriods,
    ({ byId }) => {
        const index = _groupBy(Object.values(byId), e => e.post_op_id)
        return (input: number | { id: number }) => index.get(typeof input === 'number' ? input : input.id) ?? []
    }
)

/** postOps → capacityOverrides (hasMany from postOpCapacityOverrides) */
export const selectCapacityOverridesByPostOp = createSelector(
    (state: StoreState) => state.di.entities.postOpCapacityOverrides,
    ({ byId }) => {
        const index = _groupBy(Object.values(byId), e => e.post_op_id)
        return (input: number | { id: number }) => index.get(typeof input === 'number' ? input : input.id) ?? []
    }
)

/** postOpCapacityPeriods → postOp (hasOne from postOps) */
export const selectPostOpByPostOpCapacityPeriod = createSelector(
    (state: StoreState) => state.di.entities.postOps,
    ({ byId }) =>
        (input: number | { post_op_id: number }) =>
            byId[(typeof input === 'number' ? input : input.post_op_id) as number]
)

/** postOpCapacityOverrides → postOp (hasOne from postOps) */
export const selectPostOpByPostOpCapacityOverride = createSelector(
    (state: StoreState) => state.di.entities.postOps,
    ({ byId }) =>
        (input: number | { post_op_id: number }) =>
            byId[(typeof input === 'number' ? input : input.post_op_id) as number]
)

/** locationPostOpAssociations → location (hasOne from locations) */
export const selectLocationByLocationPostOpAssociation = createSelector(
    (state: StoreState) => state.di.entities.locations,
    ({ byId }) =>
        (input: number | { location_id: number }) =>
            byId[(typeof input === 'number' ? input : input.location_id) as number]
)

/** locationPostOpAssociations → postOp (hasOne from postOps) */
export const selectPostOpByLocationPostOpAssociation = createSelector(
    (state: StoreState) => state.di.entities.postOps,
    ({ byId }) =>
        (input: number | { post_op_id: number }) =>
            byId[(typeof input === 'number' ? input : input.post_op_id) as number]
)

/** locationServiceOverrides → location (hasOne from locations) */
export const selectLocationByLocationServiceOverride = createSelector(
    (state: StoreState) => state.di.entities.locations,
    ({ byId }) => {
        const index = _keyBy(Object.values(byId), e => e.uuid)
        return (input: string | { location_uuid: string }) => index.get(typeof input === 'string' ? input : input.location_uuid)
    }
)

/** commentsThreads → messages (hasMany from commentsMessages) */
export const selectMessagesByCommentsThread = createSelector(
    (state: StoreState) => state.di.entities.commentsMessages,
    ({ byId }) => {
        const index = _groupBy(Object.values(byId), e => e.thread_id)
        return (input: number | { id: number }) => index.get(typeof input === 'number' ? input : input.id) ?? []
    }
)

/** commentsThreads → reactions (hasMany from commentsThreadReactions) */
export const selectReactionsByCommentsThread = createSelector(
    (state: StoreState) => state.di.entities.commentsThreadReactions,
    ({ byId }) => {
        const index = _groupBy(Object.values(byId), e => e.thread_id)
        return (input: number | { id: number }) => index.get(typeof input === 'number' ? input : input.id) ?? []
    }
)

/** commentsMessages → reactions (hasMany from commentsMessageReactions) */
export const selectReactionsByCommentsMessage = createSelector(
    (state: StoreState) => state.di.entities.commentsMessageReactions,
    ({ byId }) => {
        const index = _groupBy(Object.values(byId), e => e.message_id)
        return (input: number | { id: number }) => index.get(typeof input === 'number' ? input : input.id) ?? []
    }
)

/** prioritizedPatientGroups → ageGroup (hasOne from ageGroups) */
export const selectAgeGroupByPrioritizedPatientGroup = createSelector(
    (state: StoreState) => state.di.entities.ageGroups,
    ({ byId }) =>
        (input: number | { age_group_id: number }) =>
            byId[(typeof input === 'number' ? input : input.age_group_id) as number]
)

/** prioritizedPatientGroups → surgeryTypeGroup (hasOne from surgeryTypeGroups) */
export const selectSurgeryTypeGroupByPrioritizedPatientGroup = createSelector(
    (state: StoreState) => state.di.entities.surgeryTypeGroups,
    ({ byId }) =>
        (input: number | { surgery_type_group_id: number }) =>
            byId[(typeof input === 'number' ? input : input.surgery_type_group_id) as number]
)

/** practitionerProfiles → profileSurgeryTypeGroups (hasMany from practitionerProfileSurgeryTypeGroups) */
export const selectProfileSurgeryTypeGroupsByPractitionerProfile = createSelector(
    (state: StoreState) => state.di.entities.practitionerProfileSurgeryTypeGroups,
    ({ byId }) => {
        const index = _groupBy(Object.values(byId), e => e.practitioner_profile_id)
        return (input: number | { id: number }) => index.get(typeof input === 'number' ? input : input.id) ?? []
    }
)

/** practitionerProfileSurgeryTypeGroups → surgeryTypeGroup (hasOne from surgeryTypeGroups) */
export const selectSurgeryTypeGroupByPractitionerProfileSurgeryTypeGroup = createSelector(
    (state: StoreState) => state.di.entities.surgeryTypeGroups,
    ({ byId }) =>
        (input: number | { surgery_type_group_id: number }) =>
            byId[(typeof input === 'number' ? input : input.surgery_type_group_id) as number]
)

/** locationProfiles → locationSurgeryTypeGroups (hasMany from locationProfileSurgeryTypeGroups) */
export const selectLocationSurgeryTypeGroupsByLocationProfile = createSelector(
    (state: StoreState) => state.di.entities.locationProfileSurgeryTypeGroups,
    ({ byId }) => {
        const index = _groupBy(Object.values(byId), e => e.location_profile_id)
        return (input: number | { id: number }) => index.get(typeof input === 'number' ? input : input.id) ?? []
    }
)

/** locationProfileSurgeryTypeGroups → surgeryTypeGroup (hasOne from surgeryTypeGroups) */
export const selectSurgeryTypeGroupByLocationProfileSurgeryTypeGroup = createSelector(
    (state: StoreState) => state.di.entities.surgeryTypeGroups,
    ({ byId }) =>
        (input: number | { surgery_type_group_id: number }) =>
            byId[(typeof input === 'number' ? input : input.surgery_type_group_id) as number]
)

/** staffingSurgeryGroups → surgeryTypeGroupAssignments (hasMany from staffingSurgeryGroupSurgeryTypeGroups) */
export const selectSurgeryTypeGroupAssignmentsByStaffingSurgeryGroup = createSelector(
    (state: StoreState) => state.di.entities.staffingSurgeryGroupSurgeryTypeGroups,
    ({ byId }) => {
        const index = _groupBy(Object.values(byId), e => e.staffing_surgery_group_uuid)
        return (input: string | { uuid: string }) => index.get(typeof input === 'string' ? input : input.uuid) ?? []
    }
)

/** staffingSurgeryGroups → surgeryTypeGroups (hasManyThrough staffingSurgeryGroupSurgeryTypeGroups → surgeryTypeGroups) */
export const selectSurgeryTypeGroupsByStaffingSurgeryGroup = createSelector(
    (state: StoreState) => state.di.entities.staffingSurgeryGroupSurgeryTypeGroups,
    (state: StoreState) => state.di.entities.surgeryTypeGroups,
    (throughTable, targetTable) => {
        const throughIndex = _groupBy(Object.values(throughTable.byId), e => e.staffing_surgery_group_uuid)
        const targetIndex = _keyBy(Object.values(targetTable.byId), e => e.uuid)
        return (input: string | { uuid: string }) => {
            const key = typeof input === 'string' ? input : input.uuid
            const throughRecords = throughIndex.get(key) ?? []
            const results: NonNullable<(typeof targetTable.byId)[number]>[] = []
            for (const record of throughRecords) {
                const targetKey = record.surgery_type_group_uuid
                if (targetKey !== null && targetKey !== undefined) {
                    const target = targetIndex.get(targetKey)
                    if (target) results.push(target)
                }
            }
            return results
        }
    }
)

/** staffingSurgeryGroupSurgeryTypeGroups → surgeryTypeGroup (hasOne from surgeryTypeGroups) */
export const selectSurgeryTypeGroupByStaffingSurgeryGroupSurgeryTypeGroup = createSelector(
    (state: StoreState) => state.di.entities.surgeryTypeGroups,
    ({ byId }) => {
        const index = _keyBy(Object.values(byId), e => e.uuid)
        return (input: string | { surgery_type_group_uuid: string }) => index.get(typeof input === 'string' ? input : input.surgery_type_group_uuid)
    }
)

/** staffingSurgeryGroupSurgeryTypeGroups → staffingSurgeryGroup (hasOne from staffingSurgeryGroups) */
export const selectStaffingSurgeryGroupByStaffingSurgeryGroupSurgeryTypeGroup = createSelector(
    (state: StoreState) => state.di.entities.staffingSurgeryGroups,
    ({ byId }) => {
        const index = _keyBy(Object.values(byId), e => e.uuid)
        return (input: string | { staffing_surgery_group_uuid: string }) => index.get(typeof input === 'string' ? input : input.staffing_surgery_group_uuid)
    }
)

// ── Per-Entity Relations Configs ─────────────────────────────────────────

const _blockSchedulesRelations = {
    location: { accessor: (state: StoreState) => selectLocationByBlockSchedule(state), childRelations: () => _locationsRelations },
}

const _commentsRelations = {
    locationSchedule: { accessor: (state: StoreState) => selectLocationScheduleByComment(state), childRelations: () => _locationSchedulesRelations },
}

const _commentsMessagesRelations = {
    reactions: { accessor: (state: StoreState) => selectReactionsByCommentsMessage(state) },
}

const _commentsThreadsRelations = {
    messages: { accessor: (state: StoreState) => selectMessagesByCommentsThread(state), childRelations: () => _commentsMessagesRelations },
    reactions: { accessor: (state: StoreState) => selectReactionsByCommentsThread(state) },
}

const _departmentPractitionerAssignmentsRelations = {
    department: { accessor: (state: StoreState) => selectDepartmentByDepartmentPractitionerAssignment(state), childRelations: () => _departmentsRelations },
    practitioner: {
        accessor: (state: StoreState) => selectPractitionerByDepartmentPractitionerAssignment(state),
        childRelations: () => _practitionersRelations,
    },
}

const _departmentsRelations = {
    locations: { accessor: (state: StoreState) => selectLocationsByDepartment(state), childRelations: () => _locationsRelations },
    practitioners: { accessor: (state: StoreState) => selectPractitionersByDepartment(state), childRelations: () => _practitionersRelations },
    sections: { accessor: (state: StoreState) => selectSectionsByDepartment(state), childRelations: () => _sectionsRelations },
}

const _locationPostOpAssociationsRelations = {
    location: { accessor: (state: StoreState) => selectLocationByLocationPostOpAssociation(state), childRelations: () => _locationsRelations },
    postOp: { accessor: (state: StoreState) => selectPostOpByLocationPostOpAssociation(state), childRelations: () => _postOpsRelations },
}

const _locationProfilesRelations = {
    locationSurgeryTypeGroups: {
        accessor: (state: StoreState) => selectLocationSurgeryTypeGroupsByLocationProfile(state),
        childRelations: () => _locationProfileSurgeryTypeGroupsRelations,
    },
}

const _locationProfileSurgeryTypeGroupsRelations = {
    surgeryTypeGroup: {
        accessor: (state: StoreState) => selectSurgeryTypeGroupByLocationProfileSurgeryTypeGroup(state),
        childRelations: () => _surgeryTypeGroupsRelations,
    },
}

const _locationsRelations = {
    departments: { accessor: (state: StoreState) => selectDepartmentsByLocation(state), childRelations: () => _departmentsRelations },
    dipsResource: { accessor: (state: StoreState) => selectDipsResourceByLocation(state) },
    postOps: { accessor: (state: StoreState) => selectPostOpsByLocation(state), childRelations: () => _postOpsRelations },
    locationProfiles: { accessor: (state: StoreState) => selectLocationProfilesByLocation(state), childRelations: () => _locationProfilesRelations },
}

const _locationSchedulesRelations = {
    comments: { accessor: (state: StoreState) => selectCommentsByLocationSchedule(state), childRelations: () => _commentsRelations },
}

const _locationScheduleSpecialitiesRelations = {
    location: { accessor: (state: StoreState) => selectLocationByLocationScheduleSpeciality(state), childRelations: () => _locationsRelations },
    speciality: { accessor: (state: StoreState) => selectSpecialityByLocationScheduleSpeciality(state), childRelations: () => _specialitiesRelations },
}

const _locationScheduleSpecialityActivityLogsRelations = {
    speciality: {
        accessor: (state: StoreState) => selectSpecialityByLocationScheduleSpecialityActivityLog(state),
        childRelations: () => _specialitiesRelations,
    },
}

const _locationServiceOverridesRelations = {
    location: { accessor: (state: StoreState) => selectLocationByLocationServiceOverride(state), childRelations: () => _locationsRelations },
}

const _planningPeriodsRelations = {
    prioritizedPatientGroups: {
        accessor: (state: StoreState) => selectPrioritizedPatientGroupsByPlanningPeriod(state),
        childRelations: () => _prioritizedPatientGroupsRelations,
    },
}

const _postOpCapacityOverridesRelations = {
    postOp: { accessor: (state: StoreState) => selectPostOpByPostOpCapacityOverride(state), childRelations: () => _postOpsRelations },
}

const _postOpCapacityPeriodsRelations = {
    postOp: { accessor: (state: StoreState) => selectPostOpByPostOpCapacityPeriod(state), childRelations: () => _postOpsRelations },
}

const _postOpsRelations = {
    locations: { accessor: (state: StoreState) => selectLocationsByPostOp(state), childRelations: () => _locationsRelations },
    capacityPeriods: { accessor: (state: StoreState) => selectCapacityPeriodsByPostOp(state), childRelations: () => _postOpCapacityPeriodsRelations },
    capacityOverrides: { accessor: (state: StoreState) => selectCapacityOverridesByPostOp(state), childRelations: () => _postOpCapacityOverridesRelations },
}

const _practitionerProfilesRelations = {
    profileSurgeryTypeGroups: {
        accessor: (state: StoreState) => selectProfileSurgeryTypeGroupsByPractitionerProfile(state),
        childRelations: () => _practitionerProfileSurgeryTypeGroupsRelations,
    },
}

const _practitionerProfileSurgeryTypeGroupsRelations = {
    surgeryTypeGroup: {
        accessor: (state: StoreState) => selectSurgeryTypeGroupByPractitionerProfileSurgeryTypeGroup(state),
        childRelations: () => _surgeryTypeGroupsRelations,
    },
}

const _practitionersRelations = {
    dipsResource: { accessor: (state: StoreState) => selectDipsResourceByPractitioner(state) },
    departments: { accessor: (state: StoreState) => selectDepartmentsByPractitioner(state), childRelations: () => _departmentsRelations },
    departmentAssignments: {
        accessor: (state: StoreState) => selectDepartmentAssignmentsByPractitioner(state),
        childRelations: () => _departmentPractitionerAssignmentsRelations,
    },
    servicePeriods: { accessor: (state: StoreState) => selectServicePeriodsByPractitioner(state), childRelations: () => _practitionerServicePeriodsRelations },
    practitionerSchedules: { accessor: (state: StoreState) => selectPractitionerSchedulesByPractitioner(state) },
    practitionerProfiles: {
        accessor: (state: StoreState) => selectPractitionerProfilesByPractitioner(state),
        childRelations: () => _practitionerProfilesRelations,
    },
}

const _practitionerScheduleLocationActivityLogsRelations = {
    location: { accessor: (state: StoreState) => selectLocationByPractitionerScheduleLocationActivityLog(state), childRelations: () => _locationsRelations },
}

const _practitionerScheduleLocationsRelations = {
    practitioner: { accessor: (state: StoreState) => selectPractitionerByPractitionerScheduleLocation(state), childRelations: () => _practitionersRelations },
    location: { accessor: (state: StoreState) => selectLocationByPractitionerScheduleLocation(state), childRelations: () => _locationsRelations },
}

const _practitionerScheduleStatusActivityLogsRelations = {
    statusDefinition: {
        accessor: (state: StoreState) => selectStatusDefinitionByPractitionerScheduleStatusActivityLog(state),
        childRelations: () => _practitionerStatusDefinitionsRelations,
    },
}

const _practitionerScheduleStatusesRelations = {
    practitioner: { accessor: (state: StoreState) => selectPractitionerByPractitionerScheduleStatus(state), childRelations: () => _practitionersRelations },
    definition: {
        accessor: (state: StoreState) => selectDefinitionByPractitionerScheduleStatus(state),
        childRelations: () => _practitionerStatusDefinitionsRelations,
    },
}

const _practitionerServicePeriodsRelations = {
    practitioner: { accessor: (state: StoreState) => selectPractitionerByPractitionerServicePeriod(state), childRelations: () => _practitionersRelations },
}

const _practitionerStatusDefinitionsRelations = {
    departments: { accessor: (state: StoreState) => selectDepartmentsByPractitionerStatusDefinition(state), childRelations: () => _departmentsRelations },
}

const _prioritizedPatientGroupsRelations = {
    ageGroup: { accessor: (state: StoreState) => selectAgeGroupByPrioritizedPatientGroup(state) },
    surgeryTypeGroup: {
        accessor: (state: StoreState) => selectSurgeryTypeGroupByPrioritizedPatientGroup(state),
        childRelations: () => _surgeryTypeGroupsRelations,
    },
}

const _recurringAssignmentsRelations = {
    practitioner: { accessor: (state: StoreState) => selectPractitionerByRecurringAssignment(state), childRelations: () => _practitionersRelations },
    monday_status: {
        accessor: (state: StoreState) => selectMondayStatusByRecurringAssignment(state),
        childRelations: () => _practitionerStatusDefinitionsRelations,
    },
    tuesday_status: {
        accessor: (state: StoreState) => selectTuesdayStatusByRecurringAssignment(state),
        childRelations: () => _practitionerStatusDefinitionsRelations,
    },
    wednesday_status: {
        accessor: (state: StoreState) => selectWednesdayStatusByRecurringAssignment(state),
        childRelations: () => _practitionerStatusDefinitionsRelations,
    },
    thursday_status: {
        accessor: (state: StoreState) => selectThursdayStatusByRecurringAssignment(state),
        childRelations: () => _practitionerStatusDefinitionsRelations,
    },
    friday_status: {
        accessor: (state: StoreState) => selectFridayStatusByRecurringAssignment(state),
        childRelations: () => _practitionerStatusDefinitionsRelations,
    },
    saturday_status: {
        accessor: (state: StoreState) => selectSaturdayStatusByRecurringAssignment(state),
        childRelations: () => _practitionerStatusDefinitionsRelations,
    },
    sunday_status: {
        accessor: (state: StoreState) => selectSundayStatusByRecurringAssignment(state),
        childRelations: () => _practitionerStatusDefinitionsRelations,
    },
    monday_room: { accessor: (state: StoreState) => selectMondayRoomByRecurringAssignment(state), childRelations: () => _locationsRelations },
    tuesday_room: { accessor: (state: StoreState) => selectTuesdayRoomByRecurringAssignment(state), childRelations: () => _locationsRelations },
    wednesday_room: { accessor: (state: StoreState) => selectWednesdayRoomByRecurringAssignment(state), childRelations: () => _locationsRelations },
    thursday_room: { accessor: (state: StoreState) => selectThursdayRoomByRecurringAssignment(state), childRelations: () => _locationsRelations },
    friday_room: { accessor: (state: StoreState) => selectFridayRoomByRecurringAssignment(state), childRelations: () => _locationsRelations },
    saturday_room: { accessor: (state: StoreState) => selectSaturdayRoomByRecurringAssignment(state), childRelations: () => _locationsRelations },
    sunday_room: { accessor: (state: StoreState) => selectSundayRoomByRecurringAssignment(state), childRelations: () => _locationsRelations },
}

const _scheduledSurgeriesRelations = {
    surgeryFolderMetadata: { accessor: (state: StoreState) => selectSurgeryFolderMetadataByScheduledSurgery(state) },
    section: { accessor: (state: StoreState) => selectSectionByScheduledSurgery(state), childRelations: () => _sectionsRelations },
    location: { accessor: (state: StoreState) => selectLocationByScheduledSurgery(state), childRelations: () => _locationsRelations },
    surgeryMetadataSuggestions: { accessor: (state: StoreState) => selectSurgeryMetadataSuggestionsByScheduledSurgery(state) },
}

const _sectionsRelations = {
    department: { accessor: (state: StoreState) => selectDepartmentBySection(state), childRelations: () => _departmentsRelations },
    speciality: { accessor: (state: StoreState) => selectSpecialityBySection(state), childRelations: () => _specialitiesRelations },
}

const _specialitiesRelations = {
    sections: { accessor: (state: StoreState) => selectSectionsBySpeciality(state), childRelations: () => _sectionsRelations },
}

const _staffingPlanExecutionsRelations = {
    department: { accessor: (state: StoreState) => selectDepartmentByStaffingPlanExecution(state), childRelations: () => _departmentsRelations },
}

const _staffingSurgeryGroupsRelations = {
    surgeryTypeGroupAssignments: {
        accessor: (state: StoreState) => selectSurgeryTypeGroupAssignmentsByStaffingSurgeryGroup(state),
        childRelations: () => _staffingSurgeryGroupSurgeryTypeGroupsRelations,
    },
    surgeryTypeGroups: {
        accessor: (state: StoreState) => selectSurgeryTypeGroupsByStaffingSurgeryGroup(state),
        childRelations: () => _surgeryTypeGroupsRelations,
    },
}

const _staffingSurgeryGroupSurgeryTypeGroupsRelations = {
    surgeryTypeGroup: {
        accessor: (state: StoreState) => selectSurgeryTypeGroupByStaffingSurgeryGroupSurgeryTypeGroup(state),
        childRelations: () => _surgeryTypeGroupsRelations,
    },
    staffingSurgeryGroup: {
        accessor: (state: StoreState) => selectStaffingSurgeryGroupByStaffingSurgeryGroupSurgeryTypeGroup(state),
        childRelations: () => _staffingSurgeryGroupsRelations,
    },
}

const _statusDefinitionDepartmentAssignmentsRelations = {
    department: { accessor: (state: StoreState) => selectDepartmentByStatusDefinitionDepartmentAssignment(state), childRelations: () => _departmentsRelations },
    statusDefinition: {
        accessor: (state: StoreState) => selectStatusDefinitionByStatusDefinitionDepartmentAssignment(state),
        childRelations: () => _practitionerStatusDefinitionsRelations,
    },
}

const _surgeryTypeGroupsRelations = {
    ageRestrictions: { accessor: (state: StoreState) => selectAgeRestrictionsBySurgeryTypeGroup(state) },
    specialities: { accessor: (state: StoreState) => selectSpecialitiesBySurgeryTypeGroup(state), childRelations: () => _specialitiesRelations },
    subgroups: { accessor: (state: StoreState) => selectSubgroupsBySurgeryTypeGroup(state), childRelations: () => _surgeryTypeGroupsRelations },
    surgeryTypes: { accessor: (state: StoreState) => selectSurgeryTypesBySurgeryTypeGroup(state) },
}

const _unScheduledSurgeriesRelations = {
    surgeryFolderMetadata: { accessor: (state: StoreState) => selectSurgeryFolderMetadataByUnScheduledSurgery(state) },
    section: { accessor: (state: StoreState) => selectSectionByUnScheduledSurgery(state), childRelations: () => _sectionsRelations },
    surgeryMetadataSuggestions: { accessor: (state: StoreState) => selectSurgeryMetadataSuggestionsByUnScheduledSurgery(state) },
}

// ── Entity Attribute Selectors ────────────────────────────────────────────

/** Single ageGroups entity matching the supplied attrs (undefined if none). */
export const selectAgeGroup = _createSelectSingular(state => state.di.entities.ageGroups, { entityName: 'ageGroups' })
/** ageGroups matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectAgeGroups = _createSelectPlural(state => state.di.entities.ageGroups, { entityName: 'ageGroups' })
export const useAgeGroup = _createUseSingular(selectAgeGroup)
export const useAgeGroups = _createUsePlural(selectAgeGroups)

/** Single backfillQueues entity matching the supplied attrs (undefined if none). */
export const selectBackfillQueue = _createSelectSingular(state => state.di.entities.backfillQueues, { entityName: 'backfillQueues' })
/** backfillQueues matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectBackfillQueues = _createSelectPlural(state => state.di.entities.backfillQueues, { entityName: 'backfillQueues' })
export const useBackfillQueue = _createUseSingular(selectBackfillQueue)
export const useBackfillQueues = _createUsePlural(selectBackfillQueues)

/** Single blockSchedules entity matching the supplied attrs (undefined if none). */
export const selectBlockSchedule = _createSelectSingular(state => state.di.entities.blockSchedules, {
    entityName: 'blockSchedules',
    relations: _blockSchedulesRelations,
})
/** blockSchedules matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectBlockSchedules = _createSelectPlural(state => state.di.entities.blockSchedules, {
    entityName: 'blockSchedules',
    relations: _blockSchedulesRelations,
})
export const useBlockSchedule = _createUseSingular(selectBlockSchedule)
export const useBlockSchedules = _createUsePlural(selectBlockSchedules)

/** Single checklistItemDefinitions entity matching the supplied attrs (undefined if none). */
export const selectChecklistItemDefinition = _createSelectSingular(state => state.di.entities.checklistItemDefinitions, {
    entityName: 'checklistItemDefinitions',
})
/** checklistItemDefinitions matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectChecklistItemDefinitions = _createSelectPlural(state => state.di.entities.checklistItemDefinitions, {
    entityName: 'checklistItemDefinitions',
})
export const useChecklistItemDefinition = _createUseSingular(selectChecklistItemDefinition)
export const useChecklistItemDefinitions = _createUsePlural(selectChecklistItemDefinitions)

/** Single comments entity matching the supplied attrs (undefined if none). */
export const selectComment = _createSelectSingular(state => state.di.entities.comments, { entityName: 'comments', relations: _commentsRelations })
/** comments matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectComments = _createSelectPlural(state => state.di.entities.comments, { entityName: 'comments', relations: _commentsRelations })
export const useComment = _createUseSingular(selectComment)
export const useComments = _createUsePlural(selectComments)

/** Single commentsMessageReactions entity matching the supplied attrs (undefined if none). */
export const selectCommentsMessageReaction = _createSelectSingular(state => state.di.entities.commentsMessageReactions, {
    entityName: 'commentsMessageReactions',
})
/** commentsMessageReactions matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectCommentsMessageReactions = _createSelectPlural(state => state.di.entities.commentsMessageReactions, {
    entityName: 'commentsMessageReactions',
})
export const useCommentsMessageReaction = _createUseSingular(selectCommentsMessageReaction)
export const useCommentsMessageReactions = _createUsePlural(selectCommentsMessageReactions)

/** Single commentsMessages entity matching the supplied attrs (undefined if none). */
export const selectCommentsMessage = _createSelectSingular(state => state.di.entities.commentsMessages, {
    entityName: 'commentsMessages',
    relations: _commentsMessagesRelations,
})
/** commentsMessages matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectCommentsMessages = _createSelectPlural(state => state.di.entities.commentsMessages, {
    entityName: 'commentsMessages',
    relations: _commentsMessagesRelations,
})
export const useCommentsMessage = _createUseSingular(selectCommentsMessage)
export const useCommentsMessages = _createUsePlural(selectCommentsMessages)

/** Single commentsThreadReactions entity matching the supplied attrs (undefined if none). */
export const selectCommentsThreadReaction = _createSelectSingular(state => state.di.entities.commentsThreadReactions, { entityName: 'commentsThreadReactions' })
/** commentsThreadReactions matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectCommentsThreadReactions = _createSelectPlural(state => state.di.entities.commentsThreadReactions, { entityName: 'commentsThreadReactions' })
export const useCommentsThreadReaction = _createUseSingular(selectCommentsThreadReaction)
export const useCommentsThreadReactions = _createUsePlural(selectCommentsThreadReactions)

/** Single commentsThreads entity matching the supplied attrs (undefined if none). */
export const selectCommentsThread = _createSelectSingular(state => state.di.entities.commentsThreads, {
    entityName: 'commentsThreads',
    relations: _commentsThreadsRelations,
})
/** commentsThreads matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectCommentsThreads = _createSelectPlural(state => state.di.entities.commentsThreads, {
    entityName: 'commentsThreads',
    relations: _commentsThreadsRelations,
})
export const useCommentsThread = _createUseSingular(selectCommentsThread)
export const useCommentsThreads = _createUsePlural(selectCommentsThreads)

/** Single departmentLocationAssignments entity matching the supplied attrs (undefined if none). */
export const selectDepartmentLocationAssignment = _createSelectSingular(state => state.di.entities.departmentLocationAssignments, {
    entityName: 'departmentLocationAssignments',
})
/** departmentLocationAssignments matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectDepartmentLocationAssignments = _createSelectPlural(state => state.di.entities.departmentLocationAssignments, {
    entityName: 'departmentLocationAssignments',
})
export const useDepartmentLocationAssignment = _createUseSingular(selectDepartmentLocationAssignment)
export const useDepartmentLocationAssignments = _createUsePlural(selectDepartmentLocationAssignments)

/** Single departmentPractitionerAssignments entity matching the supplied attrs (undefined if none). */
export const selectDepartmentPractitionerAssignment = _createSelectSingular(state => state.di.entities.departmentPractitionerAssignments, {
    entityName: 'departmentPractitionerAssignments',
    relations: _departmentPractitionerAssignmentsRelations,
})
/** departmentPractitionerAssignments matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectDepartmentPractitionerAssignments = _createSelectPlural(state => state.di.entities.departmentPractitionerAssignments, {
    entityName: 'departmentPractitionerAssignments',
    relations: _departmentPractitionerAssignmentsRelations,
})
export const useDepartmentPractitionerAssignment = _createUseSingular(selectDepartmentPractitionerAssignment)
export const useDepartmentPractitionerAssignments = _createUsePlural(selectDepartmentPractitionerAssignments)

/** Single departments entity matching the supplied attrs (undefined if none). */
export const selectDepartment = _createSelectSingular(state => state.di.entities.departments, { entityName: 'departments', relations: _departmentsRelations })
/** departments matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectDepartments = _createSelectPlural(state => state.di.entities.departments, { entityName: 'departments', relations: _departmentsRelations })
export const useDepartment = _createUseSingular(selectDepartment)
export const useDepartments = _createUsePlural(selectDepartments)

/** Single dipsLocalizations entity matching the supplied attrs (undefined if none). */
export const selectDipsLocalization = _createSelectSingular(state => state.dips.entities.dipsLocalizations, { entityName: 'dipsLocalizations' })
/** dipsLocalizations matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectDipsLocalizations = _createSelectPlural(state => state.dips.entities.dipsLocalizations, { entityName: 'dipsLocalizations' })
export const useDipsLocalization = _createUseSingular(selectDipsLocalization)
export const useDipsLocalizations = _createUsePlural(selectDipsLocalizations)

/** Single dipsPostponementReasons entity matching the supplied attrs (undefined if none). */
export const selectDipsPostponementReason = _createSelectSingular(state => state.dips.entities.dipsPostponementReasons, {
    entityName: 'dipsPostponementReasons',
})
/** dipsPostponementReasons matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectDipsPostponementReasons = _createSelectPlural(state => state.dips.entities.dipsPostponementReasons, {
    entityName: 'dipsPostponementReasons',
})
export const useDipsPostponementReason = _createUseSingular(selectDipsPostponementReason)
export const useDipsPostponementReasons = _createUsePlural(selectDipsPostponementReasons)

/** Single dipsResources entity matching the supplied attrs (undefined if none). */
export const selectDipsResource = _createSelectSingular(state => state.dips.entities.dipsResources, { entityName: 'dipsResources' })
/** dipsResources matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectDipsResources = _createSelectPlural(state => state.dips.entities.dipsResources, { entityName: 'dipsResources' })
export const useDipsResource = _createUseSingular(selectDipsResource)
export const useDipsResources = _createUsePlural(selectDipsResources)

/** Single dipsWards entity matching the supplied attrs (undefined if none). */
export const selectDipsWard = _createSelectSingular(state => state.dips.entities.dipsWards, { entityName: 'dipsWards' })
/** dipsWards matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectDipsWards = _createSelectPlural(state => state.dips.entities.dipsWards, { entityName: 'dipsWards' })
export const useDipsWard = _createUseSingular(selectDipsWard)
export const useDipsWards = _createUsePlural(selectDipsWards)

/** Single hospitalSurgeryTypeGroupAssociations entity matching the supplied attrs (undefined if none). */
export const selectHospitalSurgeryTypeGroupAssociation = _createSelectSingular(state => state.di.entities.hospitalSurgeryTypeGroupAssociations, {
    entityName: 'hospitalSurgeryTypeGroupAssociations',
})
/** hospitalSurgeryTypeGroupAssociations matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectHospitalSurgeryTypeGroupAssociations = _createSelectPlural(state => state.di.entities.hospitalSurgeryTypeGroupAssociations, {
    entityName: 'hospitalSurgeryTypeGroupAssociations',
})
export const useHospitalSurgeryTypeGroupAssociation = _createUseSingular(selectHospitalSurgeryTypeGroupAssociation)
export const useHospitalSurgeryTypeGroupAssociations = _createUsePlural(selectHospitalSurgeryTypeGroupAssociations)

/** Single hospitalSurgeryTypes entity matching the supplied attrs (undefined if none). */
export const selectHospitalSurgeryType = _createSelectSingular(state => state.di.entities.hospitalSurgeryTypes, { entityName: 'hospitalSurgeryTypes' })
/** hospitalSurgeryTypes matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectHospitalSurgeryTypes = _createSelectPlural(state => state.di.entities.hospitalSurgeryTypes, { entityName: 'hospitalSurgeryTypes' })
export const useHospitalSurgeryType = _createUseSingular(selectHospitalSurgeryType)
export const useHospitalSurgeryTypes = _createUsePlural(selectHospitalSurgeryTypes)

/** Single inboxEntries entity matching the supplied attrs (undefined if none). */
export const selectInboxEntry = _createSelectSingular(state => state.di.entities.inboxEntries, { entityName: 'inboxEntries' })
/** inboxEntries matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectInboxEntries = _createSelectPlural(state => state.di.entities.inboxEntries, { entityName: 'inboxEntries' })
export const useInboxEntry = _createUseSingular(selectInboxEntry)
export const useInboxEntries = _createUsePlural(selectInboxEntries)

/** Single locationPostOpAssociations entity matching the supplied attrs (undefined if none). */
export const selectLocationPostOpAssociation = _createSelectSingular(state => state.di.entities.locationPostOpAssociations, {
    entityName: 'locationPostOpAssociations',
    relations: _locationPostOpAssociationsRelations,
})
/** locationPostOpAssociations matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectLocationPostOpAssociations = _createSelectPlural(state => state.di.entities.locationPostOpAssociations, {
    entityName: 'locationPostOpAssociations',
    relations: _locationPostOpAssociationsRelations,
})
export const useLocationPostOpAssociation = _createUseSingular(selectLocationPostOpAssociation)
export const useLocationPostOpAssociations = _createUsePlural(selectLocationPostOpAssociations)

/** Single locationProfiles entity matching the supplied attrs (undefined if none). */
export const selectLocationProfile = _createSelectSingular(state => state.di.entities.locationProfiles, {
    entityName: 'locationProfiles',
    relations: _locationProfilesRelations,
})
/** locationProfiles matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectLocationProfiles = _createSelectPlural(state => state.di.entities.locationProfiles, {
    entityName: 'locationProfiles',
    relations: _locationProfilesRelations,
})
export const useLocationProfile = _createUseSingular(selectLocationProfile)
export const useLocationProfiles = _createUsePlural(selectLocationProfiles)

/** Single locationProfileSurgeryTypeGroups entity matching the supplied attrs (undefined if none). */
export const selectLocationProfileSurgeryTypeGroup = _createSelectSingular(state => state.di.entities.locationProfileSurgeryTypeGroups, {
    entityName: 'locationProfileSurgeryTypeGroups',
    relations: _locationProfileSurgeryTypeGroupsRelations,
})
/** locationProfileSurgeryTypeGroups matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectLocationProfileSurgeryTypeGroups = _createSelectPlural(state => state.di.entities.locationProfileSurgeryTypeGroups, {
    entityName: 'locationProfileSurgeryTypeGroups',
    relations: _locationProfileSurgeryTypeGroupsRelations,
})
export const useLocationProfileSurgeryTypeGroup = _createUseSingular(selectLocationProfileSurgeryTypeGroup)
export const useLocationProfileSurgeryTypeGroups = _createUsePlural(selectLocationProfileSurgeryTypeGroups)

/** Single locations entity matching the supplied attrs (undefined if none). */
export const selectLocation = _createSelectSingular(state => state.di.entities.locations, { entityName: 'locations', relations: _locationsRelations })
/** locations matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectLocations = _createSelectPlural(state => state.di.entities.locations, { entityName: 'locations', relations: _locationsRelations })
export const useLocation = _createUseSingular(selectLocation)
export const useLocations = _createUsePlural(selectLocations)

/** Single locationScheduleActivityLogs entity matching the supplied attrs (undefined if none). */
export const selectLocationScheduleActivityLog = _createSelectSingular(state => state.di.entities.locationScheduleActivityLogs, {
    entityName: 'locationScheduleActivityLogs',
})
/** locationScheduleActivityLogs matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectLocationScheduleActivityLogs = _createSelectPlural(state => state.di.entities.locationScheduleActivityLogs, {
    entityName: 'locationScheduleActivityLogs',
})
export const useLocationScheduleActivityLog = _createUseSingular(selectLocationScheduleActivityLog)
export const useLocationScheduleActivityLogs = _createUsePlural(selectLocationScheduleActivityLogs)

/** Single locationSchedules entity matching the supplied attrs (undefined if none). */
export const selectLocationSchedule = _createSelectSingular(state => state.di.entities.locationSchedules, {
    entityName: 'locationSchedules',
    relations: _locationSchedulesRelations,
})
/** locationSchedules matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectLocationSchedules = _createSelectPlural(state => state.di.entities.locationSchedules, {
    entityName: 'locationSchedules',
    relations: _locationSchedulesRelations,
})
export const useLocationSchedule = _createUseSingular(selectLocationSchedule)
export const useLocationSchedules = _createUsePlural(selectLocationSchedules)

/** Single locationScheduleSpecialities entity matching the supplied attrs (undefined if none). */
export const selectLocationScheduleSpeciality = _createSelectSingular(state => state.di.entities.locationScheduleSpecialities, {
    entityName: 'locationScheduleSpecialities',
    relations: _locationScheduleSpecialitiesRelations,
})
/** locationScheduleSpecialities matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectLocationScheduleSpecialities = _createSelectPlural(state => state.di.entities.locationScheduleSpecialities, {
    entityName: 'locationScheduleSpecialities',
    relations: _locationScheduleSpecialitiesRelations,
})
export const useLocationScheduleSpeciality = _createUseSingular(selectLocationScheduleSpeciality)
export const useLocationScheduleSpecialities = _createUsePlural(selectLocationScheduleSpecialities)

/** Single locationScheduleSpecialityActivityLogs entity matching the supplied attrs (undefined if none). */
export const selectLocationScheduleSpecialityActivityLog = _createSelectSingular(state => state.di.entities.locationScheduleSpecialityActivityLogs, {
    entityName: 'locationScheduleSpecialityActivityLogs',
    relations: _locationScheduleSpecialityActivityLogsRelations,
})
/** locationScheduleSpecialityActivityLogs matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectLocationScheduleSpecialityActivityLogs = _createSelectPlural(state => state.di.entities.locationScheduleSpecialityActivityLogs, {
    entityName: 'locationScheduleSpecialityActivityLogs',
    relations: _locationScheduleSpecialityActivityLogsRelations,
})
export const useLocationScheduleSpecialityActivityLog = _createUseSingular(selectLocationScheduleSpecialityActivityLog)
export const useLocationScheduleSpecialityActivityLogs = _createUsePlural(selectLocationScheduleSpecialityActivityLogs)

/** Single locationServiceOverrides entity matching the supplied attrs (undefined if none). */
export const selectLocationServiceOverride = _createSelectSingular(state => state.di.entities.locationServiceOverrides, {
    entityName: 'locationServiceOverrides',
    relations: _locationServiceOverridesRelations,
})
/** locationServiceOverrides matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectLocationServiceOverrides = _createSelectPlural(state => state.di.entities.locationServiceOverrides, {
    entityName: 'locationServiceOverrides',
    relations: _locationServiceOverridesRelations,
})
export const useLocationServiceOverride = _createUseSingular(selectLocationServiceOverride)
export const useLocationServiceOverrides = _createUsePlural(selectLocationServiceOverrides)

/** Single mailingList entity matching the supplied attrs (undefined if none). */
export const selectEmail = _createSelectSingular(state => state.di.entities.mailingList, { entityName: 'mailingList' })
/** mailingList matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectMailingList = _createSelectPlural(state => state.di.entities.mailingList, { entityName: 'mailingList' })
export const useEmail = _createUseSingular(selectEmail)
export const useMailingList = _createUsePlural(selectMailingList)

/** Single planningDomains entity matching the supplied attrs (undefined if none). */
export const selectPlanningDomain = _createSelectSingular(state => state.di.entities.planningDomains, { entityName: 'planningDomains' })
/** planningDomains matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectPlanningDomains = _createSelectPlural(state => state.di.entities.planningDomains, { entityName: 'planningDomains' })
export const usePlanningDomain = _createUseSingular(selectPlanningDomain)
export const usePlanningDomains = _createUsePlural(selectPlanningDomains)

/** Single planningNoteHistoryRecords entity matching the supplied attrs (undefined if none). */
export const selectPlanningNoteHistoryRecord = _createSelectSingular(state => state.di.entities.planningNoteHistoryRecords, {
    entityName: 'planningNoteHistoryRecords',
})
/** planningNoteHistoryRecords matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectPlanningNoteHistoryRecords = _createSelectPlural(state => state.di.entities.planningNoteHistoryRecords, {
    entityName: 'planningNoteHistoryRecords',
})
export const usePlanningNoteHistoryRecord = _createUseSingular(selectPlanningNoteHistoryRecord)
export const usePlanningNoteHistoryRecords = _createUsePlural(selectPlanningNoteHistoryRecords)

/** Single planningNoteMigrations entity matching the supplied attrs (undefined if none). */
export const selectPlanningNoteMigrationRecord = _createSelectSingular(state => state.di.entities.planningNoteMigrations, {
    entityName: 'planningNoteMigrations',
})
/** planningNoteMigrations matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectPlanningNoteMigrations = _createSelectPlural(state => state.di.entities.planningNoteMigrations, { entityName: 'planningNoteMigrations' })
export const usePlanningNoteMigrationRecord = _createUseSingular(selectPlanningNoteMigrationRecord)
export const usePlanningNoteMigrations = _createUsePlural(selectPlanningNoteMigrations)

/** Single planningPeriods entity matching the supplied attrs (undefined if none). */
export const selectPlanningPeriod = _createSelectSingular(state => state.di.entities.planningPeriods, {
    entityName: 'planningPeriods',
    relations: _planningPeriodsRelations,
})
/** planningPeriods matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectPlanningPeriods = _createSelectPlural(state => state.di.entities.planningPeriods, {
    entityName: 'planningPeriods',
    relations: _planningPeriodsRelations,
})
export const usePlanningPeriod = _createUseSingular(selectPlanningPeriod)
export const usePlanningPeriods = _createUsePlural(selectPlanningPeriods)

/** Single postOpCapacityOverrides entity matching the supplied attrs (undefined if none). */
export const selectPostOpCapacityOverride = _createSelectSingular(state => state.di.entities.postOpCapacityOverrides, {
    entityName: 'postOpCapacityOverrides',
    relations: _postOpCapacityOverridesRelations,
})
/** postOpCapacityOverrides matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectPostOpCapacityOverrides = _createSelectPlural(state => state.di.entities.postOpCapacityOverrides, {
    entityName: 'postOpCapacityOverrides',
    relations: _postOpCapacityOverridesRelations,
})
export const usePostOpCapacityOverride = _createUseSingular(selectPostOpCapacityOverride)
export const usePostOpCapacityOverrides = _createUsePlural(selectPostOpCapacityOverrides)

/** Single postOpCapacityPeriods entity matching the supplied attrs (undefined if none). */
export const selectPostOpCapacityPeriod = _createSelectSingular(state => state.di.entities.postOpCapacityPeriods, {
    entityName: 'postOpCapacityPeriods',
    relations: _postOpCapacityPeriodsRelations,
})
/** postOpCapacityPeriods matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectPostOpCapacityPeriods = _createSelectPlural(state => state.di.entities.postOpCapacityPeriods, {
    entityName: 'postOpCapacityPeriods',
    relations: _postOpCapacityPeriodsRelations,
})
export const usePostOpCapacityPeriod = _createUseSingular(selectPostOpCapacityPeriod)
export const usePostOpCapacityPeriods = _createUsePlural(selectPostOpCapacityPeriods)

/** Single postOps entity matching the supplied attrs (undefined if none). */
export const selectPostOp = _createSelectSingular(state => state.di.entities.postOps, { entityName: 'postOps', relations: _postOpsRelations })
/** postOps matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectPostOps = _createSelectPlural(state => state.di.entities.postOps, { entityName: 'postOps', relations: _postOpsRelations })
export const usePostOp = _createUseSingular(selectPostOp)
export const usePostOps = _createUsePlural(selectPostOps)

/** Single practitionerProfiles entity matching the supplied attrs (undefined if none). */
export const selectPractitionerProfile = _createSelectSingular(state => state.di.entities.practitionerProfiles, {
    entityName: 'practitionerProfiles',
    relations: _practitionerProfilesRelations,
})
/** practitionerProfiles matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectPractitionerProfiles = _createSelectPlural(state => state.di.entities.practitionerProfiles, {
    entityName: 'practitionerProfiles',
    relations: _practitionerProfilesRelations,
})
export const usePractitionerProfile = _createUseSingular(selectPractitionerProfile)
export const usePractitionerProfiles = _createUsePlural(selectPractitionerProfiles)

/** Single practitionerProfileSurgeryTypeGroups entity matching the supplied attrs (undefined if none). */
export const selectPractitionerProfileSurgeryTypeGroup = _createSelectSingular(state => state.di.entities.practitionerProfileSurgeryTypeGroups, {
    entityName: 'practitionerProfileSurgeryTypeGroups',
    relations: _practitionerProfileSurgeryTypeGroupsRelations,
})
/** practitionerProfileSurgeryTypeGroups matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectPractitionerProfileSurgeryTypeGroups = _createSelectPlural(state => state.di.entities.practitionerProfileSurgeryTypeGroups, {
    entityName: 'practitionerProfileSurgeryTypeGroups',
    relations: _practitionerProfileSurgeryTypeGroupsRelations,
})
export const usePractitionerProfileSurgeryTypeGroup = _createUseSingular(selectPractitionerProfileSurgeryTypeGroup)
export const usePractitionerProfileSurgeryTypeGroups = _createUsePlural(selectPractitionerProfileSurgeryTypeGroups)

/** Single practitioners entity matching the supplied attrs (undefined if none). */
export const selectPractitioner = _createSelectSingular(state => state.di.entities.practitioners, {
    defaultOrderBy: ['short_name'],
    entityName: 'practitioners',
    relations: _practitionersRelations,
})
/** practitioners matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectPractitioners = _createSelectPlural(state => state.di.entities.practitioners, {
    defaultOrderBy: ['short_name'],
    entityName: 'practitioners',
    relations: _practitionersRelations,
})
export const usePractitioner = _createUseSingular(selectPractitioner)
export const usePractitioners = _createUsePlural(selectPractitioners)

/** Single practitionerScheduleActivityLogs entity matching the supplied attrs (undefined if none). */
export const selectPractitionerScheduleActivityLog = _createSelectSingular(state => state.di.entities.practitionerScheduleActivityLogs, {
    entityName: 'practitionerScheduleActivityLogs',
})
/** practitionerScheduleActivityLogs matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectPractitionerScheduleActivityLogs = _createSelectPlural(state => state.di.entities.practitionerScheduleActivityLogs, {
    entityName: 'practitionerScheduleActivityLogs',
})
export const usePractitionerScheduleActivityLog = _createUseSingular(selectPractitionerScheduleActivityLog)
export const usePractitionerScheduleActivityLogs = _createUsePlural(selectPractitionerScheduleActivityLogs)

/** Single practitionerScheduleLocationActivityLogs entity matching the supplied attrs (undefined if none). */
export const selectPractitionerScheduleLocationActivityLog = _createSelectSingular(state => state.di.entities.practitionerScheduleLocationActivityLogs, {
    entityName: 'practitionerScheduleLocationActivityLogs',
    relations: _practitionerScheduleLocationActivityLogsRelations,
})
/** practitionerScheduleLocationActivityLogs matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectPractitionerScheduleLocationActivityLogs = _createSelectPlural(state => state.di.entities.practitionerScheduleLocationActivityLogs, {
    entityName: 'practitionerScheduleLocationActivityLogs',
    relations: _practitionerScheduleLocationActivityLogsRelations,
})
export const usePractitionerScheduleLocationActivityLog = _createUseSingular(selectPractitionerScheduleLocationActivityLog)
export const usePractitionerScheduleLocationActivityLogs = _createUsePlural(selectPractitionerScheduleLocationActivityLogs)

/** Single practitionerScheduleLocations entity matching the supplied attrs (undefined if none). */
export const selectPractitionerScheduleLocation = _createSelectSingular(state => state.di.entities.practitionerScheduleLocations, {
    entityName: 'practitionerScheduleLocations',
    relations: _practitionerScheduleLocationsRelations,
})
/** practitionerScheduleLocations matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectPractitionerScheduleLocations = _createSelectPlural(state => state.di.entities.practitionerScheduleLocations, {
    entityName: 'practitionerScheduleLocations',
    relations: _practitionerScheduleLocationsRelations,
})
export const usePractitionerScheduleLocation = _createUseSingular(selectPractitionerScheduleLocation)
export const usePractitionerScheduleLocations = _createUsePlural(selectPractitionerScheduleLocations)

/** Single practitionerSchedules entity matching the supplied attrs (undefined if none). */
export const selectPractitionerSchedule = _createSelectSingular(state => state.di.entities.practitionerSchedules, { entityName: 'practitionerSchedules' })
/** practitionerSchedules matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectPractitionerSchedules = _createSelectPlural(state => state.di.entities.practitionerSchedules, { entityName: 'practitionerSchedules' })
export const usePractitionerSchedule = _createUseSingular(selectPractitionerSchedule)
export const usePractitionerSchedules = _createUsePlural(selectPractitionerSchedules)

/** Single practitionerScheduleStatusActivityLogs entity matching the supplied attrs (undefined if none). */
export const selectPractitionerScheduleStatusActivityLog = _createSelectSingular(state => state.di.entities.practitionerScheduleStatusActivityLogs, {
    entityName: 'practitionerScheduleStatusActivityLogs',
    relations: _practitionerScheduleStatusActivityLogsRelations,
})
/** practitionerScheduleStatusActivityLogs matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectPractitionerScheduleStatusActivityLogs = _createSelectPlural(state => state.di.entities.practitionerScheduleStatusActivityLogs, {
    entityName: 'practitionerScheduleStatusActivityLogs',
    relations: _practitionerScheduleStatusActivityLogsRelations,
})
export const usePractitionerScheduleStatusActivityLog = _createUseSingular(selectPractitionerScheduleStatusActivityLog)
export const usePractitionerScheduleStatusActivityLogs = _createUsePlural(selectPractitionerScheduleStatusActivityLogs)

/** Single practitionerScheduleStatuses entity matching the supplied attrs (undefined if none). */
export const selectPractitionerScheduleStatus = _createSelectSingular(state => state.di.entities.practitionerScheduleStatuses, {
    defaultOrderBy: [['scheduled_date', 'asc']],
    entityName: 'practitionerScheduleStatuses',
    relations: _practitionerScheduleStatusesRelations,
})
/** practitionerScheduleStatuses matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectPractitionerScheduleStatuses = _createSelectPlural(state => state.di.entities.practitionerScheduleStatuses, {
    defaultOrderBy: [['scheduled_date', 'asc']],
    entityName: 'practitionerScheduleStatuses',
    relations: _practitionerScheduleStatusesRelations,
})
export const usePractitionerScheduleStatus = _createUseSingular(selectPractitionerScheduleStatus)
export const usePractitionerScheduleStatuses = _createUsePlural(selectPractitionerScheduleStatuses)

/** Single practitionerServicePeriods entity matching the supplied attrs (undefined if none). */
export const selectPractitionerServicePeriod = _createSelectSingular(state => state.di.entities.practitionerServicePeriods, {
    entityName: 'practitionerServicePeriods',
    relations: _practitionerServicePeriodsRelations,
})
/** practitionerServicePeriods matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectPractitionerServicePeriods = _createSelectPlural(state => state.di.entities.practitionerServicePeriods, {
    entityName: 'practitionerServicePeriods',
    relations: _practitionerServicePeriodsRelations,
})
export const usePractitionerServicePeriod = _createUseSingular(selectPractitionerServicePeriod)
export const usePractitionerServicePeriods = _createUsePlural(selectPractitionerServicePeriods)

/** Single practitionerStatusDefinitions entity matching the supplied attrs (undefined if none). */
export const selectPractitionerStatusDefinition = _createSelectSingular(state => state.di.entities.practitionerStatusDefinitions, {
    entityName: 'practitionerStatusDefinitions',
    relations: _practitionerStatusDefinitionsRelations,
})
/** practitionerStatusDefinitions matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectPractitionerStatusDefinitions = _createSelectPlural(state => state.di.entities.practitionerStatusDefinitions, {
    entityName: 'practitionerStatusDefinitions',
    relations: _practitionerStatusDefinitionsRelations,
})
export const usePractitionerStatusDefinition = _createUseSingular(selectPractitionerStatusDefinition)
export const usePractitionerStatusDefinitions = _createUsePlural(selectPractitionerStatusDefinitions)

/** Single prioritizedPatientGroups entity matching the supplied attrs (undefined if none). */
export const selectPrioritizedPatientGroup = _createSelectSingular(state => state.di.entities.prioritizedPatientGroups, {
    entityName: 'prioritizedPatientGroups',
    relations: _prioritizedPatientGroupsRelations,
})
/** prioritizedPatientGroups matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectPrioritizedPatientGroups = _createSelectPlural(state => state.di.entities.prioritizedPatientGroups, {
    entityName: 'prioritizedPatientGroups',
    relations: _prioritizedPatientGroupsRelations,
})
export const usePrioritizedPatientGroup = _createUseSingular(selectPrioritizedPatientGroup)
export const usePrioritizedPatientGroups = _createUsePlural(selectPrioritizedPatientGroups)

/** Single recurringAssignments entity matching the supplied attrs (undefined if none). */
export const selectRecurringAssignment = _createSelectSingular(state => state.di.entities.recurringAssignments, {
    entityName: 'recurringAssignments',
    relations: _recurringAssignmentsRelations,
})
/** recurringAssignments matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectRecurringAssignments = _createSelectPlural(state => state.di.entities.recurringAssignments, {
    entityName: 'recurringAssignments',
    relations: _recurringAssignmentsRelations,
})
export const useRecurringAssignment = _createUseSingular(selectRecurringAssignment)
export const useRecurringAssignments = _createUsePlural(selectRecurringAssignments)

/** Single scheduledSurgeries entity matching the supplied attrs (undefined if none). */
export const selectScheduledSurgery = _createSelectSingular(state => state.dips.entities.scheduledSurgeries, {
    entityName: 'scheduledSurgeries',
    relations: _scheduledSurgeriesRelations,
})
/** scheduledSurgeries matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectScheduledSurgeries = _createSelectPlural(state => state.dips.entities.scheduledSurgeries, {
    entityName: 'scheduledSurgeries',
    relations: _scheduledSurgeriesRelations,
})
export const useScheduledSurgery = _createUseSingular(selectScheduledSurgery)
export const useScheduledSurgeries = _createUsePlural(selectScheduledSurgeries)

/** Single sections entity matching the supplied attrs (undefined if none). */
export const selectSection = _createSelectSingular(state => state.di.entities.sections, { entityName: 'sections', relations: _sectionsRelations })
/** sections matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectSections = _createSelectPlural(state => state.di.entities.sections, { entityName: 'sections', relations: _sectionsRelations })
export const useSection = _createUseSingular(selectSection)
export const useSections = _createUsePlural(selectSections)

/** Single specialities entity matching the supplied attrs (undefined if none). */
export const selectSpeciality = _createSelectSingular(state => state.di.entities.specialities, {
    entityName: 'specialities',
    relations: _specialitiesRelations,
})
/** specialities matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectSpecialities = _createSelectPlural(state => state.di.entities.specialities, {
    entityName: 'specialities',
    relations: _specialitiesRelations,
})
export const useSpeciality = _createUseSingular(selectSpeciality)
export const useSpecialities = _createUsePlural(selectSpecialities)

/** Single staffingPlanExecutions entity matching the supplied attrs (undefined if none). */
export const selectStaffingPlanExecution = _createSelectSingular(state => state.di.entities.staffingPlanExecutions, {
    entityName: 'staffingPlanExecutions',
    relations: _staffingPlanExecutionsRelations,
})
/** staffingPlanExecutions matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectStaffingPlanExecutions = _createSelectPlural(state => state.di.entities.staffingPlanExecutions, {
    entityName: 'staffingPlanExecutions',
    relations: _staffingPlanExecutionsRelations,
})
export const useStaffingPlanExecution = _createUseSingular(selectStaffingPlanExecution)
export const useStaffingPlanExecutions = _createUsePlural(selectStaffingPlanExecutions)

/** Single staffingSurgeryGroups entity matching the supplied attrs (undefined if none). */
export const selectStaffingSurgeryGroup = _createSelectSingular(state => state.di.entities.staffingSurgeryGroups, {
    entityName: 'staffingSurgeryGroups',
    relations: _staffingSurgeryGroupsRelations,
})
/** staffingSurgeryGroups matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectStaffingSurgeryGroups = _createSelectPlural(state => state.di.entities.staffingSurgeryGroups, {
    entityName: 'staffingSurgeryGroups',
    relations: _staffingSurgeryGroupsRelations,
})
export const useStaffingSurgeryGroup = _createUseSingular(selectStaffingSurgeryGroup)
export const useStaffingSurgeryGroups = _createUsePlural(selectStaffingSurgeryGroups)

/** Single staffingSurgeryGroupSurgeryTypeGroups entity matching the supplied attrs (undefined if none). */
export const selectStaffingSurgeryGroupSurgeryTypeGroup = _createSelectSingular(state => state.di.entities.staffingSurgeryGroupSurgeryTypeGroups, {
    entityName: 'staffingSurgeryGroupSurgeryTypeGroups',
    relations: _staffingSurgeryGroupSurgeryTypeGroupsRelations,
})
/** staffingSurgeryGroupSurgeryTypeGroups matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectStaffingSurgeryGroupSurgeryTypeGroups = _createSelectPlural(state => state.di.entities.staffingSurgeryGroupSurgeryTypeGroups, {
    entityName: 'staffingSurgeryGroupSurgeryTypeGroups',
    relations: _staffingSurgeryGroupSurgeryTypeGroupsRelations,
})
export const useStaffingSurgeryGroupSurgeryTypeGroup = _createUseSingular(selectStaffingSurgeryGroupSurgeryTypeGroup)
export const useStaffingSurgeryGroupSurgeryTypeGroups = _createUsePlural(selectStaffingSurgeryGroupSurgeryTypeGroups)

/** Single statusDefinitionDepartmentAssignments entity matching the supplied attrs (undefined if none). */
export const selectStatusDefinitionDepartmentAssignment = _createSelectSingular(state => state.di.entities.statusDefinitionDepartmentAssignments, {
    entityName: 'statusDefinitionDepartmentAssignments',
    relations: _statusDefinitionDepartmentAssignmentsRelations,
})
/** statusDefinitionDepartmentAssignments matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectStatusDefinitionDepartmentAssignments = _createSelectPlural(state => state.di.entities.statusDefinitionDepartmentAssignments, {
    entityName: 'statusDefinitionDepartmentAssignments',
    relations: _statusDefinitionDepartmentAssignmentsRelations,
})
export const useStatusDefinitionDepartmentAssignment = _createUseSingular(selectStatusDefinitionDepartmentAssignment)
export const useStatusDefinitionDepartmentAssignments = _createUsePlural(selectStatusDefinitionDepartmentAssignments)

/** Single surgeryFolderMetadata entity matching the supplied attrs (undefined if none). */
export const selectSurgeryFolderMetadatum = _createSelectSingular(state => state.di.entities.surgeryFolderMetadata, { entityName: 'surgeryFolderMetadata' })
/** surgeryFolderMetadata matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectSurgeryFolderMetadata = _createSelectPlural(state => state.di.entities.surgeryFolderMetadata, { entityName: 'surgeryFolderMetadata' })
export const useSurgeryFolderMetadatum = _createUseSingular(selectSurgeryFolderMetadatum)
export const useSurgeryFolderMetadata = _createUsePlural(selectSurgeryFolderMetadata)

/** Single surgeryMetadataSuggestions entity matching the supplied attrs (undefined if none). */
export const selectSurgeryMetadataSuggestion = _createSelectSingular(state => state.di.entities.surgeryMetadataSuggestions, {
    entityName: 'surgeryMetadataSuggestions',
})
/** surgeryMetadataSuggestions matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectSurgeryMetadataSuggestions = _createSelectPlural(state => state.di.entities.surgeryMetadataSuggestions, {
    entityName: 'surgeryMetadataSuggestions',
})
export const useSurgeryMetadataSuggestion = _createUseSingular(selectSurgeryMetadataSuggestion)
export const useSurgeryMetadataSuggestions = _createUsePlural(selectSurgeryMetadataSuggestions)

/** Single surgeryTypeGroupAgeRestrictions entity matching the supplied attrs (undefined if none). */
export const selectSurgeryTypeGroupAgeRestriction = _createSelectSingular(state => state.di.entities.surgeryTypeGroupAgeRestrictions, {
    entityName: 'surgeryTypeGroupAgeRestrictions',
})
/** surgeryTypeGroupAgeRestrictions matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectSurgeryTypeGroupAgeRestrictions = _createSelectPlural(state => state.di.entities.surgeryTypeGroupAgeRestrictions, {
    entityName: 'surgeryTypeGroupAgeRestrictions',
})
export const useSurgeryTypeGroupAgeRestriction = _createUseSingular(selectSurgeryTypeGroupAgeRestriction)
export const useSurgeryTypeGroupAgeRestrictions = _createUsePlural(selectSurgeryTypeGroupAgeRestrictions)

/** Single surgeryTypeGroupHierarchies entity matching the supplied attrs (undefined if none). */
export const selectSurgeryTypeGroupHierarchy = _createSelectSingular(state => state.di.entities.surgeryTypeGroupHierarchies, {
    entityName: 'surgeryTypeGroupHierarchies',
})
/** surgeryTypeGroupHierarchies matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectSurgeryTypeGroupHierarchies = _createSelectPlural(state => state.di.entities.surgeryTypeGroupHierarchies, {
    entityName: 'surgeryTypeGroupHierarchies',
})
export const useSurgeryTypeGroupHierarchy = _createUseSingular(selectSurgeryTypeGroupHierarchy)
export const useSurgeryTypeGroupHierarchies = _createUsePlural(selectSurgeryTypeGroupHierarchies)

/** Single surgeryTypeGroups entity matching the supplied attrs (undefined if none). */
export const selectSurgeryTypeGroup = _createSelectSingular(state => state.di.entities.surgeryTypeGroups, {
    entityName: 'surgeryTypeGroups',
    relations: _surgeryTypeGroupsRelations,
})
/** surgeryTypeGroups matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectSurgeryTypeGroups = _createSelectPlural(state => state.di.entities.surgeryTypeGroups, {
    entityName: 'surgeryTypeGroups',
    relations: _surgeryTypeGroupsRelations,
})
export const useSurgeryTypeGroup = _createUseSingular(selectSurgeryTypeGroup)
export const useSurgeryTypeGroups = _createUsePlural(selectSurgeryTypeGroups)

/** Single surgeryTypeGroupSpecialities entity matching the supplied attrs (undefined if none). */
export const selectSurgeryTypeGroupSpeciality = _createSelectSingular(state => state.di.entities.surgeryTypeGroupSpecialities, {
    entityName: 'surgeryTypeGroupSpecialities',
})
/** surgeryTypeGroupSpecialities matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectSurgeryTypeGroupSpecialities = _createSelectPlural(state => state.di.entities.surgeryTypeGroupSpecialities, {
    entityName: 'surgeryTypeGroupSpecialities',
})
export const useSurgeryTypeGroupSpeciality = _createUseSingular(selectSurgeryTypeGroupSpeciality)
export const useSurgeryTypeGroupSpecialities = _createUsePlural(selectSurgeryTypeGroupSpecialities)

/** Single tacticalPlanSnapshots entity matching the supplied attrs (undefined if none). */
export const selectTacticalPlanSnapshot = _createSelectSingular(state => state.di.entities.tacticalPlanSnapshots, { entityName: 'tacticalPlanSnapshots' })
/** tacticalPlanSnapshots matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectTacticalPlanSnapshots = _createSelectPlural(state => state.di.entities.tacticalPlanSnapshots, { entityName: 'tacticalPlanSnapshots' })
export const useTacticalPlanSnapshot = _createUseSingular(selectTacticalPlanSnapshot)
export const useTacticalPlanSnapshots = _createUsePlural(selectTacticalPlanSnapshots)

/** Single unScheduledSurgeries entity matching the supplied attrs (undefined if none). */
export const selectUnScheduledSurgery = _createSelectSingular(state => state.dips.entities.unScheduledSurgeries, {
    entityName: 'unScheduledSurgeries',
    relations: _unScheduledSurgeriesRelations,
})
/** unScheduledSurgeries matching the supplied attrs; with no/empty attrs returns the full default-ordered list. */
export const selectUnScheduledSurgeries = _createSelectPlural(state => state.dips.entities.unScheduledSurgeries, {
    entityName: 'unScheduledSurgeries',
    relations: _unScheduledSurgeriesRelations,
})
export const useUnScheduledSurgery = _createUseSingular(selectUnScheduledSurgery)
export const useUnScheduledSurgeries = _createUsePlural(selectUnScheduledSurgeries)

export type AgeGroup = NonNullable<ReturnType<typeof useAgeGroup>>
export type BackfillQueue = NonNullable<ReturnType<typeof useBackfillQueue>>
export type BlockSchedule = NonNullable<ReturnType<typeof useBlockSchedule>>
export type ChecklistItemDefinition = NonNullable<ReturnType<typeof useChecklistItemDefinition>>
export type Comment = NonNullable<ReturnType<typeof useComment>>
export type CommentsMessageReaction = NonNullable<ReturnType<typeof useCommentsMessageReaction>>
export type CommentsMessage = NonNullable<ReturnType<typeof useCommentsMessage>>
export type CommentsThreadReaction = NonNullable<ReturnType<typeof useCommentsThreadReaction>>
export type CommentsThread = NonNullable<ReturnType<typeof useCommentsThread>>
export type DepartmentLocationAssignment = NonNullable<ReturnType<typeof useDepartmentLocationAssignment>>
export type DepartmentPractitionerAssignment = NonNullable<ReturnType<typeof useDepartmentPractitionerAssignment>>
export type Department = NonNullable<ReturnType<typeof useDepartment>>
export type DipsLocalization = NonNullable<ReturnType<typeof useDipsLocalization>>
export type DipsPostponementReason = NonNullable<ReturnType<typeof useDipsPostponementReason>>
export type DipsResource = NonNullable<ReturnType<typeof useDipsResource>>
export type DipsWard = NonNullable<ReturnType<typeof useDipsWard>>
export type HospitalSurgeryTypeGroupAssociation = NonNullable<ReturnType<typeof useHospitalSurgeryTypeGroupAssociation>>
export type HospitalSurgeryType = NonNullable<ReturnType<typeof useHospitalSurgeryType>>
export type InboxEntry = NonNullable<ReturnType<typeof useInboxEntry>>
export type LocationPostOpAssociation = NonNullable<ReturnType<typeof useLocationPostOpAssociation>>
export type LocationProfile = NonNullable<ReturnType<typeof useLocationProfile>>
export type LocationProfileSurgeryTypeGroup = NonNullable<ReturnType<typeof useLocationProfileSurgeryTypeGroup>>
export type Location = NonNullable<ReturnType<typeof useLocation>>
export type LocationScheduleActivityLog = NonNullable<ReturnType<typeof useLocationScheduleActivityLog>>
export type LocationSchedule = NonNullable<ReturnType<typeof useLocationSchedule>>
export type LocationScheduleSpeciality = NonNullable<ReturnType<typeof useLocationScheduleSpeciality>>
export type LocationScheduleSpecialityActivityLog = NonNullable<ReturnType<typeof useLocationScheduleSpecialityActivityLog>>
export type LocationServiceOverride = NonNullable<ReturnType<typeof useLocationServiceOverride>>
export type Email = NonNullable<ReturnType<typeof useEmail>>
export type PlanningDomain = NonNullable<ReturnType<typeof usePlanningDomain>>
export type PlanningNoteHistoryRecord = NonNullable<ReturnType<typeof usePlanningNoteHistoryRecord>>
export type PlanningNoteMigrationRecord = NonNullable<ReturnType<typeof usePlanningNoteMigrationRecord>>
export type PlanningPeriod = NonNullable<ReturnType<typeof usePlanningPeriod>>
export type PostOpCapacityOverride = NonNullable<ReturnType<typeof usePostOpCapacityOverride>>
export type PostOpCapacityPeriod = NonNullable<ReturnType<typeof usePostOpCapacityPeriod>>
export type PostOp = NonNullable<ReturnType<typeof usePostOp>>
export type PractitionerProfile = NonNullable<ReturnType<typeof usePractitionerProfile>>
export type PractitionerProfileSurgeryTypeGroup = NonNullable<ReturnType<typeof usePractitionerProfileSurgeryTypeGroup>>
export type Practitioner = NonNullable<ReturnType<typeof usePractitioner>>
export type PractitionerScheduleActivityLog = NonNullable<ReturnType<typeof usePractitionerScheduleActivityLog>>
export type PractitionerScheduleLocationActivityLog = NonNullable<ReturnType<typeof usePractitionerScheduleLocationActivityLog>>
export type PractitionerScheduleLocation = NonNullable<ReturnType<typeof usePractitionerScheduleLocation>>
export type PractitionerSchedule = NonNullable<ReturnType<typeof usePractitionerSchedule>>
export type PractitionerScheduleStatusActivityLog = NonNullable<ReturnType<typeof usePractitionerScheduleStatusActivityLog>>
export type PractitionerScheduleStatus = NonNullable<ReturnType<typeof usePractitionerScheduleStatus>>
export type PractitionerServicePeriod = NonNullable<ReturnType<typeof usePractitionerServicePeriod>>
export type PractitionerStatusDefinition = NonNullable<ReturnType<typeof usePractitionerStatusDefinition>>
export type PrioritizedPatientGroup = NonNullable<ReturnType<typeof usePrioritizedPatientGroup>>
export type RecurringAssignment = NonNullable<ReturnType<typeof useRecurringAssignment>>
export type ScheduledSurgery = NonNullable<ReturnType<typeof useScheduledSurgery>>
export type Section = NonNullable<ReturnType<typeof useSection>>
export type Speciality = NonNullable<ReturnType<typeof useSpeciality>>
export type StaffingPlanExecution = NonNullable<ReturnType<typeof useStaffingPlanExecution>>
export type StaffingSurgeryGroup = NonNullable<ReturnType<typeof useStaffingSurgeryGroup>>
export type StaffingSurgeryGroupSurgeryTypeGroup = NonNullable<ReturnType<typeof useStaffingSurgeryGroupSurgeryTypeGroup>>
export type StatusDefinitionDepartmentAssignment = NonNullable<ReturnType<typeof useStatusDefinitionDepartmentAssignment>>
export type SurgeryFolderMetadatum = NonNullable<ReturnType<typeof useSurgeryFolderMetadatum>>
export type SurgeryMetadataSuggestion = NonNullable<ReturnType<typeof useSurgeryMetadataSuggestion>>
export type SurgeryTypeGroupAgeRestriction = NonNullable<ReturnType<typeof useSurgeryTypeGroupAgeRestriction>>
export type SurgeryTypeGroupHierarchy = NonNullable<ReturnType<typeof useSurgeryTypeGroupHierarchy>>
export type SurgeryTypeGroup = NonNullable<ReturnType<typeof useSurgeryTypeGroup>>
export type SurgeryTypeGroupSpeciality = NonNullable<ReturnType<typeof useSurgeryTypeGroupSpeciality>>
export type TacticalPlanSnapshot = NonNullable<ReturnType<typeof useTacticalPlanSnapshot>>
export type UnScheduledSurgery = NonNullable<ReturnType<typeof useUnScheduledSurgery>>
// ── EntityRelations Augmentation (drives nested-include typing) ──────────
declare module './shallow-selectors.helpers' {
    interface EntityRelations {
        blockSchedules: {
            location: { value: ReturnType<ReturnType<typeof selectLocationByBlockSchedule>>; entity: 'locations' }
        }
        comments: {
            locationSchedule: { value: ReturnType<ReturnType<typeof selectLocationScheduleByComment>>; entity: 'locationSchedules' }
        }
        commentsMessages: {
            reactions: { value: ReturnType<ReturnType<typeof selectReactionsByCommentsMessage>>; entity: 'commentsMessageReactions' }
        }
        commentsThreads: {
            messages: { value: ReturnType<ReturnType<typeof selectMessagesByCommentsThread>>; entity: 'commentsMessages' }
            reactions: { value: ReturnType<ReturnType<typeof selectReactionsByCommentsThread>>; entity: 'commentsThreadReactions' }
        }
        departmentPractitionerAssignments: {
            department: { value: ReturnType<ReturnType<typeof selectDepartmentByDepartmentPractitionerAssignment>>; entity: 'departments' }
            practitioner: { value: ReturnType<ReturnType<typeof selectPractitionerByDepartmentPractitionerAssignment>>; entity: 'practitioners' }
        }
        departments: {
            locations: { value: ReturnType<ReturnType<typeof selectLocationsByDepartment>>; entity: 'locations' }
            practitioners: { value: ReturnType<ReturnType<typeof selectPractitionersByDepartment>>; entity: 'practitioners' }
            sections: { value: ReturnType<ReturnType<typeof selectSectionsByDepartment>>; entity: 'sections' }
        }
        locationPostOpAssociations: {
            location: { value: ReturnType<ReturnType<typeof selectLocationByLocationPostOpAssociation>>; entity: 'locations' }
            postOp: { value: ReturnType<ReturnType<typeof selectPostOpByLocationPostOpAssociation>>; entity: 'postOps' }
        }
        locationProfiles: {
            locationSurgeryTypeGroups: {
                value: ReturnType<ReturnType<typeof selectLocationSurgeryTypeGroupsByLocationProfile>>
                entity: 'locationProfileSurgeryTypeGroups'
            }
        }
        locationProfileSurgeryTypeGroups: {
            surgeryTypeGroup: { value: ReturnType<ReturnType<typeof selectSurgeryTypeGroupByLocationProfileSurgeryTypeGroup>>; entity: 'surgeryTypeGroups' }
        }
        locations: {
            departments: { value: ReturnType<ReturnType<typeof selectDepartmentsByLocation>>; entity: 'departments' }
            dipsResource: { value: ReturnType<ReturnType<typeof selectDipsResourceByLocation>>; entity: 'dipsResources' }
            postOps: { value: ReturnType<ReturnType<typeof selectPostOpsByLocation>>; entity: 'postOps' }
            locationProfiles: { value: ReturnType<ReturnType<typeof selectLocationProfilesByLocation>>; entity: 'locationProfiles' }
        }
        locationSchedules: {
            comments: { value: ReturnType<ReturnType<typeof selectCommentsByLocationSchedule>>; entity: 'comments' }
        }
        locationScheduleSpecialities: {
            location: { value: ReturnType<ReturnType<typeof selectLocationByLocationScheduleSpeciality>>; entity: 'locations' }
            speciality: { value: ReturnType<ReturnType<typeof selectSpecialityByLocationScheduleSpeciality>>; entity: 'specialities' }
        }
        locationScheduleSpecialityActivityLogs: {
            speciality: { value: ReturnType<ReturnType<typeof selectSpecialityByLocationScheduleSpecialityActivityLog>>; entity: 'specialities' }
        }
        locationServiceOverrides: {
            location: { value: ReturnType<ReturnType<typeof selectLocationByLocationServiceOverride>>; entity: 'locations' }
        }
        planningPeriods: {
            prioritizedPatientGroups: {
                value: ReturnType<ReturnType<typeof selectPrioritizedPatientGroupsByPlanningPeriod>>
                entity: 'prioritizedPatientGroups'
            }
        }
        postOpCapacityOverrides: {
            postOp: { value: ReturnType<ReturnType<typeof selectPostOpByPostOpCapacityOverride>>; entity: 'postOps' }
        }
        postOpCapacityPeriods: {
            postOp: { value: ReturnType<ReturnType<typeof selectPostOpByPostOpCapacityPeriod>>; entity: 'postOps' }
        }
        postOps: {
            locations: { value: ReturnType<ReturnType<typeof selectLocationsByPostOp>>; entity: 'locations' }
            capacityPeriods: { value: ReturnType<ReturnType<typeof selectCapacityPeriodsByPostOp>>; entity: 'postOpCapacityPeriods' }
            capacityOverrides: { value: ReturnType<ReturnType<typeof selectCapacityOverridesByPostOp>>; entity: 'postOpCapacityOverrides' }
        }
        practitionerProfiles: {
            profileSurgeryTypeGroups: {
                value: ReturnType<ReturnType<typeof selectProfileSurgeryTypeGroupsByPractitionerProfile>>
                entity: 'practitionerProfileSurgeryTypeGroups'
            }
        }
        practitionerProfileSurgeryTypeGroups: {
            surgeryTypeGroup: { value: ReturnType<ReturnType<typeof selectSurgeryTypeGroupByPractitionerProfileSurgeryTypeGroup>>; entity: 'surgeryTypeGroups' }
        }
        practitioners: {
            dipsResource: { value: ReturnType<ReturnType<typeof selectDipsResourceByPractitioner>>; entity: 'dipsResources' }
            departments: { value: ReturnType<ReturnType<typeof selectDepartmentsByPractitioner>>; entity: 'departments' }
            departmentAssignments: {
                value: ReturnType<ReturnType<typeof selectDepartmentAssignmentsByPractitioner>>
                entity: 'departmentPractitionerAssignments'
            }
            servicePeriods: { value: ReturnType<ReturnType<typeof selectServicePeriodsByPractitioner>>; entity: 'practitionerServicePeriods' }
            practitionerSchedules: { value: ReturnType<ReturnType<typeof selectPractitionerSchedulesByPractitioner>>; entity: 'practitionerSchedules' }
            practitionerProfiles: { value: ReturnType<ReturnType<typeof selectPractitionerProfilesByPractitioner>>; entity: 'practitionerProfiles' }
        }
        practitionerScheduleLocationActivityLogs: {
            location: { value: ReturnType<ReturnType<typeof selectLocationByPractitionerScheduleLocationActivityLog>>; entity: 'locations' }
        }
        practitionerScheduleLocations: {
            practitioner: { value: ReturnType<ReturnType<typeof selectPractitionerByPractitionerScheduleLocation>>; entity: 'practitioners' }
            location: { value: ReturnType<ReturnType<typeof selectLocationByPractitionerScheduleLocation>>; entity: 'locations' }
        }
        practitionerScheduleStatusActivityLogs: {
            statusDefinition: {
                value: ReturnType<ReturnType<typeof selectStatusDefinitionByPractitionerScheduleStatusActivityLog>>
                entity: 'practitionerStatusDefinitions'
            }
        }
        practitionerScheduleStatuses: {
            practitioner: { value: ReturnType<ReturnType<typeof selectPractitionerByPractitionerScheduleStatus>>; entity: 'practitioners' }
            definition: { value: ReturnType<ReturnType<typeof selectDefinitionByPractitionerScheduleStatus>>; entity: 'practitionerStatusDefinitions' }
        }
        practitionerServicePeriods: {
            practitioner: { value: ReturnType<ReturnType<typeof selectPractitionerByPractitionerServicePeriod>>; entity: 'practitioners' }
        }
        practitionerStatusDefinitions: {
            departments: { value: ReturnType<ReturnType<typeof selectDepartmentsByPractitionerStatusDefinition>>; entity: 'departments' }
        }
        prioritizedPatientGroups: {
            ageGroup: { value: ReturnType<ReturnType<typeof selectAgeGroupByPrioritizedPatientGroup>>; entity: 'ageGroups' }
            surgeryTypeGroup: { value: ReturnType<ReturnType<typeof selectSurgeryTypeGroupByPrioritizedPatientGroup>>; entity: 'surgeryTypeGroups' }
        }
        recurringAssignments: {
            practitioner: { value: ReturnType<ReturnType<typeof selectPractitionerByRecurringAssignment>>; entity: 'practitioners' }
            monday_status: { value: ReturnType<ReturnType<typeof selectMondayStatusByRecurringAssignment>>; entity: 'practitionerStatusDefinitions' }
            tuesday_status: { value: ReturnType<ReturnType<typeof selectTuesdayStatusByRecurringAssignment>>; entity: 'practitionerStatusDefinitions' }
            wednesday_status: { value: ReturnType<ReturnType<typeof selectWednesdayStatusByRecurringAssignment>>; entity: 'practitionerStatusDefinitions' }
            thursday_status: { value: ReturnType<ReturnType<typeof selectThursdayStatusByRecurringAssignment>>; entity: 'practitionerStatusDefinitions' }
            friday_status: { value: ReturnType<ReturnType<typeof selectFridayStatusByRecurringAssignment>>; entity: 'practitionerStatusDefinitions' }
            saturday_status: { value: ReturnType<ReturnType<typeof selectSaturdayStatusByRecurringAssignment>>; entity: 'practitionerStatusDefinitions' }
            sunday_status: { value: ReturnType<ReturnType<typeof selectSundayStatusByRecurringAssignment>>; entity: 'practitionerStatusDefinitions' }
            monday_room: { value: ReturnType<ReturnType<typeof selectMondayRoomByRecurringAssignment>>; entity: 'locations' }
            tuesday_room: { value: ReturnType<ReturnType<typeof selectTuesdayRoomByRecurringAssignment>>; entity: 'locations' }
            wednesday_room: { value: ReturnType<ReturnType<typeof selectWednesdayRoomByRecurringAssignment>>; entity: 'locations' }
            thursday_room: { value: ReturnType<ReturnType<typeof selectThursdayRoomByRecurringAssignment>>; entity: 'locations' }
            friday_room: { value: ReturnType<ReturnType<typeof selectFridayRoomByRecurringAssignment>>; entity: 'locations' }
            saturday_room: { value: ReturnType<ReturnType<typeof selectSaturdayRoomByRecurringAssignment>>; entity: 'locations' }
            sunday_room: { value: ReturnType<ReturnType<typeof selectSundayRoomByRecurringAssignment>>; entity: 'locations' }
        }
        scheduledSurgeries: {
            surgeryFolderMetadata: { value: ReturnType<ReturnType<typeof selectSurgeryFolderMetadataByScheduledSurgery>>; entity: 'surgeryFolderMetadata' }
            section: { value: ReturnType<ReturnType<typeof selectSectionByScheduledSurgery>>; entity: 'sections' }
            location: { value: ReturnType<ReturnType<typeof selectLocationByScheduledSurgery>>; entity: 'locations' }
            surgeryMetadataSuggestions: {
                value: ReturnType<ReturnType<typeof selectSurgeryMetadataSuggestionsByScheduledSurgery>>
                entity: 'surgeryMetadataSuggestions'
            }
        }
        sections: {
            department: { value: ReturnType<ReturnType<typeof selectDepartmentBySection>>; entity: 'departments' }
            speciality: { value: ReturnType<ReturnType<typeof selectSpecialityBySection>>; entity: 'specialities' }
        }
        specialities: {
            sections: { value: ReturnType<ReturnType<typeof selectSectionsBySpeciality>>; entity: 'sections' }
        }
        staffingPlanExecutions: {
            department: { value: ReturnType<ReturnType<typeof selectDepartmentByStaffingPlanExecution>>; entity: 'departments' }
        }
        staffingSurgeryGroups: {
            surgeryTypeGroupAssignments: {
                value: ReturnType<ReturnType<typeof selectSurgeryTypeGroupAssignmentsByStaffingSurgeryGroup>>
                entity: 'staffingSurgeryGroupSurgeryTypeGroups'
            }
            surgeryTypeGroups: { value: ReturnType<ReturnType<typeof selectSurgeryTypeGroupsByStaffingSurgeryGroup>>; entity: 'surgeryTypeGroups' }
        }
        staffingSurgeryGroupSurgeryTypeGroups: {
            surgeryTypeGroup: {
                value: ReturnType<ReturnType<typeof selectSurgeryTypeGroupByStaffingSurgeryGroupSurgeryTypeGroup>>
                entity: 'surgeryTypeGroups'
            }
            staffingSurgeryGroup: {
                value: ReturnType<ReturnType<typeof selectStaffingSurgeryGroupByStaffingSurgeryGroupSurgeryTypeGroup>>
                entity: 'staffingSurgeryGroups'
            }
        }
        statusDefinitionDepartmentAssignments: {
            department: { value: ReturnType<ReturnType<typeof selectDepartmentByStatusDefinitionDepartmentAssignment>>; entity: 'departments' }
            statusDefinition: {
                value: ReturnType<ReturnType<typeof selectStatusDefinitionByStatusDefinitionDepartmentAssignment>>
                entity: 'practitionerStatusDefinitions'
            }
        }
        surgeryTypeGroups: {
            ageRestrictions: { value: ReturnType<ReturnType<typeof selectAgeRestrictionsBySurgeryTypeGroup>>; entity: 'ageGroups' }
            specialities: { value: ReturnType<ReturnType<typeof selectSpecialitiesBySurgeryTypeGroup>>; entity: 'specialities' }
            subgroups: { value: ReturnType<ReturnType<typeof selectSubgroupsBySurgeryTypeGroup>>; entity: 'surgeryTypeGroups' }
            surgeryTypes: { value: ReturnType<ReturnType<typeof selectSurgeryTypesBySurgeryTypeGroup>>; entity: 'hospitalSurgeryTypeGroupAssociations' }
        }
        unScheduledSurgeries: {
            surgeryFolderMetadata: { value: ReturnType<ReturnType<typeof selectSurgeryFolderMetadataByUnScheduledSurgery>>; entity: 'surgeryFolderMetadata' }
            section: { value: ReturnType<ReturnType<typeof selectSectionByUnScheduledSurgery>>; entity: 'sections' }
            surgeryMetadataSuggestions: {
                value: ReturnType<ReturnType<typeof selectSurgeryMetadataSuggestionsByUnScheduledSurgery>>
                entity: 'surgeryMetadataSuggestions'
            }
        }
    }
}
